import { useEffect, useState, useCallback } from 'react';
import { Ticket, Resource, TicketNote, TimeEntry, TimeTechProject, IntakeSession, DeviceScanResult, Contact, FCREmail, FCREmailAttachment } from '../services/types';
import { ProjectStatusPanel } from './ProjectStatusPanel';
import { cleanNoteBody, isSystemNoise } from './notes/cleanNote';
import { getResources, updateTicket, addTicketNote, addTimeEntry, refreshTicket, getTicketNotes, getTicketTimeEntries, getCompanyTicketsForMerge, mergeTicket, getTicketByNumber, getTimeTechProjectsForCompany, getBidJobsForCompany, getDefaultBidJob, BidJob, snoozeTicket, nudgeTicket, markTicketAsSpam, toggleProjectFlag, getTicketFieldOptions, TicketFieldOption, getDiagnosticDevices, createDiagnosticSession, getDiagnosticSession, getDiagnosticSessionsForTicket, TamDevice, TamCustomer, DiagnosticSession, DiagnosticSessionStatus, createMileageEntry, createExpenseEntry, getMileageEstimate, getSystemSetting, getActiveCompanies, CompanyOption, createIntakeSession, getIntakeSessionsForTicket, manageIntakeSession, getDeviceScanData, triggerDeviceScan, getCompanyContacts, createContact, getTicketOriginalEmail, setTicketAutoClose } from '../services/api';
import { useAuth } from '../contexts/AuthProvider';
import { useToast } from '../contexts/ToastProvider';

interface TicketDetailModalProps {
  ticket: Ticket | null;
  isOpen: boolean;
  onClose: () => void;
  onTicketUpdated?: () => void;
  onTicketCompleted?: (ticketId: string) => void;
}

// Fallback status options (will be replaced by dynamic fetch)
const DEFAULT_STATUS_OPTIONS: TicketFieldOption[] = [
  { id: 1, name: 'New', isActive: true, isSystem: false },
  { id: 8, name: 'In Progress', isActive: true, isSystem: false },
  { id: 7, name: 'Waiting Customer', isActive: true, isSystem: false },
  { id: 12, name: 'Waiting On Us', isActive: true, isSystem: false },
  { id: 5, name: 'Complete', isActive: true, isSystem: false },
];

// Fallback priority options (will be replaced by dynamic fetch)
const DEFAULT_PRIORITY_OPTIONS: TicketFieldOption[] = [
  { id: 4, name: 'Critical', isActive: true, isSystem: false },
  { id: 3, name: 'High', isActive: true, isSystem: false },
  { id: 2, name: 'Medium', isActive: true, isSystem: false },
  { id: 1, name: 'Low', isActive: true, isSystem: false },
];

/**
 * Determine TimeTech project based on bid-job type
 * - PCP bid-jobs (Proactive Checks) -> "Proactive Checks" project
 * - Other bid-jobs (Help Desk) -> "Help Desk" project
 * Returns matching project or null if no matching project found
 */
function getProjectForBidJob(
  bidJob: { bidJobNumber?: string } | null,
  projects: TimeTechProject[]
): TimeTechProject | null {
  if (!bidJob || !bidJob.bidJobNumber || projects.length === 0) {
    return null;
  }

  const isPCPBidJob = bidJob.bidJobNumber.toUpperCase().startsWith('PCP');
  const targetProjectName = isPCPBidJob ? 'Proactive Checks' : 'Help Desk';

  // Find project by name (case-insensitive)
  return projects.find(
    p => p.projectName.toLowerCase() === targetProjectName.toLowerCase() && p.isActive
  ) || null;
}


// Priority badge colors
function getPriorityColor(priority: string): string {
  switch (priority?.toLowerCase()) {
    case 'critical':
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    case 'high':
      return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    case 'medium':
      return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'low':
      return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    default:
      return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  }
}

// Status badge colors
function getStatusColor(status: string): string {
  switch (status?.toLowerCase()) {
    case 'new':
      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'in progress':
      return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'waiting customer':
      return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'waiting on us':
      return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    case 'complete':
    case 'closed':
      return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    default:
      return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  }
}

// Format time ago
function formatTimeAgo(dateStr: string | undefined): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Loading dots animation
function LoadingDots() {
  return (
    <span className="inline-flex items-center text-yellow-400">
      <span className="animate-pulse">.</span>
      <span className="animate-pulse" style={{ animationDelay: '0.2s' }}>.</span>
      <span className="animate-pulse" style={{ animationDelay: '0.4s' }}>.</span>
    </span>
  );
}

type ActiveTab = 'details' | 'notes' | 'time' | 'intake';

export default function TicketDetailModal({ ticket: initialTicket, isOpen, onClose, onTicketUpdated, onTicketCompleted }: TicketDetailModalProps) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<ActiveTab>('details');
  const [resources, setResources] = useState<Resource[]>([]);
  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(null);
  // myResourceId = the logged-in user's own resource (for time entries, notes, snooze, nudge)
  // selectedResourceId = who to reassign to (Details tab dropdown)
  const [myResourceId, setMyResourceId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Refreshed ticket data from AutoTask
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Note form state
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState<number>(2); // Default: Customer Visible

  // Time entry form state
  const [hoursWorked, setHoursWorked] = useState<string>('');
  const [timeNotes, setTimeNotes] = useState('');
  const [closeAfterTime, setCloseAfterTime] = useState(false);
  const [dateWorked, setDateWorked] = useState<string>(() => new Date().toISOString().split('T')[0]);

  // Existing notes and time entries
  const [existingNotes, setExistingNotes] = useState<TicketNote[]>([]);
  // Notes collapsible state — track which note bodies are expanded. All
  // collapsed by default per jgoode 6/17 ("show date+author, click to read body").
  const [expandedNoteIds, setExpandedNoteIds] = useState<Set<string | number>>(new Set());
  // Show workflow-rule audit + email-from stub notes (hidden by default).
  const [showSystemNotes, setShowSystemNotes] = useState(false);
  // Per-note "show full quoted thread" toggle (default: cleaned, no quoted history).
  const [rawNoteIds, setRawNoteIds] = useState<Set<string | number>>(new Set());
  const [existingTimeEntries, setExistingTimeEntries] = useState<TimeEntry[]>([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [isLoadingTimeEntries, setIsLoadingTimeEntries] = useState(false);

  // FCR original email viewer
  const [showOriginalEmail, setShowOriginalEmail] = useState(false);
  const [originalEmail, setOriginalEmail] = useState<FCREmail | null>(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [modalExpanded, setModalExpanded] = useState(false);

  // Merge ticket state
  const [showMergeDropdown, setShowMergeDropdown] = useState(false);
  const [mergeTickets, setMergeTickets] = useState<Ticket[]>([]);
  const [isLoadingMergeTickets, setIsLoadingMergeTickets] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [manualTicketNumber, setManualTicketNumber] = useState('');
  const [manualTicketSearching, setManualTicketSearching] = useState(false);
  const [manualTicketError, setManualTicketError] = useState<string | null>(null);
  const [foundManualTicket, setFoundManualTicket] = useState<Ticket | null>(null);

  // Pending completion state - when user selects Complete, we require time entry first
  const [pendingCompletion, setPendingCompletion] = useState(false);

  // Snooze state
  const [isSnoozing, setIsSnoozing] = useState(false);
  const [isAutoClosing, setIsAutoClosing] = useState(false);
  const [showAutoCloseDate, setShowAutoCloseDate] = useState(false);
  const [autoCloseDateInput, setAutoCloseDateInput] = useState('');

  // Spam action state
  const [isMarkingSpam, setIsMarkingSpam] = useState(false);

  // v1.67.0: unverified caller verification (beside Assign)
  const [callerFirst, setCallerFirst] = useState('');
  const [callerLast, setCallerLast] = useState('');
  const [callerEmail, setCallerEmail] = useState('');
  const [isConfirmingCaller, setIsConfirmingCaller] = useState(false);

  // Intake form state
  const [intakeSessions, setIntakeSessions] = useState<IntakeSession[]>([]);
  const [isLoadingIntake, setIsLoadingIntake] = useState(false);
  const [intakeEndUserName, setIntakeEndUserName] = useState('');
  const [intakeEndUserEmail, setIntakeEndUserEmail] = useState('');
  const [isSendingIntake, setIsSendingIntake] = useState(false);
  const [expandedIntakeId, setExpandedIntakeId] = useState<string | null>(null);
  const [intakeScanData, setIntakeScanData] = useState<Record<number, DeviceScanResult | null>>({});
  const [screenConnectBaseUrl, setScreenConnectBaseUrl] = useState('https://umetech.screenconnect.com');

  // Contact dropdown state for intake
  const [companyContacts, setCompanyContacts] = useState<Contact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newContactFirstName, setNewContactFirstName] = useState('');
  const [newContactLastName, setNewContactLastName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);
  const [creatingContact, setCreatingContact] = useState(false);
  const [savingContactToTicket, setSavingContactToTicket] = useState(false);
  const [showInfoBarNewContact, setShowInfoBarNewContact] = useState(false);

  // Nudge action state
  const [isNudging, setIsNudging] = useState(false);

  // TimeTech project state (for clients that require SourceTypeId=3)
  const [projects, setProjects] = useState<TimeTechProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<TimeTechProject | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Bid-job state
  const [bidJobs, setBidJobs] = useState<BidJob[]>([]);
  const [selectedBidJob, setSelectedBidJob] = useState<BidJob | null>(null);
  const [loadingBidJobs, setLoadingBidJobs] = useState(false);

  // Field options (status, priority) from AutoTask
  const [statusOptions, setStatusOptions] = useState<TicketFieldOption[]>(DEFAULT_STATUS_OPTIONS);
  const [priorityOptions, setPriorityOptions] = useState<TicketFieldOption[]>(DEFAULT_PRIORITY_OPTIONS);

  // Diagnostic section state
  const [diagnosticsExpanded, setDiagnosticsExpanded] = useState(false);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [diagnosticsError, setDiagnosticsError] = useState<string | null>(null);
  const [tamCustomer, setTamCustomer] = useState<TamCustomer | null>(null);
  const [tamDevices, setTamDevices] = useState<TamDevice[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<TamDevice | null>(null);
  const [diagnosticTechNotes, setDiagnosticTechNotes] = useState('');
  const [diagnosticSession, setDiagnosticSession] = useState<DiagnosticSession | null>(null);
  const [existingSessions, setExistingSessions] = useState<DiagnosticSession[]>([]);
  const [creatingSession, setCreatingSession] = useState(false);
  const [pollingSession, setPollingSession] = useState(false);

  // Mileage & expense state
  const [showMileage, setShowMileage] = useState(false);
  const [showExpense, setShowExpense] = useState(false);
  const [mileageStart, setMileageStart] = useState<string>('Office');
  const [mileageDestination, setMileageDestination] = useState<string>('');
  const [odometerStart, setOdometerStart] = useState<string>('');
  const [odometerEnd, setOdometerEnd] = useState<string>('');
  const [mileageItem, setMileageItem] = useState<string>('');
  const [estimatedMiles, setEstimatedMiles] = useState<number | null>(null);
  const [mileageRate, setMileageRate] = useState<number>(0.725);
  const [expenseItem, setExpenseItem] = useState<string>('');
  const [expenseAmount, setExpenseAmount] = useState<string>('');

  // Company reassignment state (for "Unknown Customer" tickets)
  const [showCompanyPicker, setShowCompanyPicker] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [reassigningCompany, setReassigningCompany] = useState(false);

  // Load resources and field options on mount
  useEffect(() => {
    async function loadResources() {
      try {
        const data = await getResources();
        // Filter to only actual technicians:
        // - Allow licenseType 1, 2, or undefined (techs without licenseType set)
        // - Exclude licenseType 3 = API users
        // - Exclude autoTaskId 4 = system account
        // - Exclude names containing "API" as extra safety
        const filteredResources = data.filter(r =>
          r.isActive &&
          r.licenseType !== 3 &&
          r.autoTaskId !== 4 &&
          !(`${r.firstName} ${r.lastName}`.toLowerCase().includes('api'))
        );
        setResources(filteredResources);

        // Always detect the logged-in user's own resource by email (for time entries, notes)
        // This prevents cross-user contamination from localStorage
        let detectedMyResource: Resource | undefined;
        if (user?.email && user.email !== 'embed@hud.local') {
          detectedMyResource = filteredResources.find(
            r => r.email?.toLowerCase() === user.email.toLowerCase()
          );
          if (detectedMyResource) {
            setMyResourceId(detectedMyResource.autoTaskId);
            localStorage.setItem('autotask-hub-resource-id', String(detectedMyResource.autoTaskId));
          }
        } else {
          // Embed mode fallback: use stored resource ID
          const savedResourceId = localStorage.getItem('autotask-hub-resource-id');
          if (savedResourceId) {
            const id = parseInt(savedResourceId, 10);
            setMyResourceId(id);
            detectedMyResource = filteredResources.find(r => r.autoTaskId === id);
          }
        }
        // Default the reassign dropdown to the logged-in user as well
        if (detectedMyResource) {
          setSelectedResourceId(detectedMyResource.autoTaskId);
        }
      } catch (err) {
        console.error('Failed to load resources:', err);
      }
    }

    async function loadFieldOptions() {
      try {
        const options = await getTicketFieldOptions();
        // Filter to only show relevant statuses for the dropdown
        const relevantStatuses = ['New', 'In Progress', 'Waiting Customer', 'Waiting On Us', 'Complete'];
        const filteredStatuses = options.statuses.filter(s => relevantStatuses.includes(s.name));
        if (filteredStatuses.length > 0) {
          setStatusOptions(filteredStatuses);
        }
        if (options.priorities.length > 0) {
          setPriorityOptions(options.priorities);
        }
      } catch (err) {
        console.error('Failed to load field options:', err);
        // Keep using defaults on error
      }
    }

    if (isOpen) {
      loadResources();
      loadFieldOptions();
    }
  }, [isOpen]);

  // Re-detect myResourceId when user identity arrives via postMessage
  // (embed mode: user starts as embed@hud.local, then HUD_USER_AUTH updates email)
  useEffect(() => {
    if (!user?.email || user.email === 'embed@hud.local' || resources.length === 0) return;
    const match = resources.find(r => r.email?.toLowerCase() === user.email.toLowerCase());
    if (match && match.autoTaskId !== myResourceId) {
      setMyResourceId(match.autoTaskId);
      localStorage.setItem('autotask-hub-resource-id', String(match.autoTaskId));
    }
  }, [user?.email, resources]);

  // Load company contacts when modal opens (for contact dropdown in info bar)
  useEffect(() => {
    if (!isOpen || !initialTicket?.companyId) return;
    setLoadingContacts(true);
    getCompanyContacts(initialTicket.companyId, true)
      .then(contacts => {
        setCompanyContacts(contacts);
        if (initialTicket.contactId) {
          const match = contacts.find(c => c.id === initialTicket.contactId);
          if (match) setSelectedContactId(match.id);
        } else if (initialTicket.contactEmail) {
          const match = contacts.find(c => c.email.toLowerCase() === initialTicket.contactEmail!.toLowerCase());
          if (match) setSelectedContactId(match.id);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingContacts(false));
  }, [isOpen, initialTicket?.companyId]);

  // Load mileage rate when modal opens
  useEffect(() => {
    if (!isOpen) return;
    getSystemSetting<number>('mileageRatePerMile')
      .then(rate => { if (rate) setMileageRate(rate); })
      .catch(() => { /* use default */ });
  }, [isOpen]);

  // Reset mileage/expense state when modal opens with new ticket
  useEffect(() => {
    if (isOpen && initialTicket) {
      setShowMileage(false);
      setShowExpense(false);
      setMileageStart('Office');
      setMileageDestination(initialTicket.companyName || '');
      setOdometerStart('');
      setOdometerEnd('');
      setMileageItem('');
      setEstimatedMiles(null);
      setExpenseItem('');
      setExpenseAmount('');
    }
  }, [isOpen, initialTicket?.autoTaskId]);

  // Fetch mileage estimate when mileage panel is shown
  useEffect(() => {
    if (!showMileage || !initialTicket?.companyId) {
      setEstimatedMiles(null);
      return;
    }
    const from: 'office' | number = mileageStart === 'Office' ? 'office' : initialTicket.companyId;
    getMileageEstimate(from, initialTicket.companyId)
      .then(result => setEstimatedMiles(result.estimatedMiles > 0 ? result.estimatedMiles : null))
      .catch(() => setEstimatedMiles(null));
  }, [showMileage, mileageStart, initialTicket?.companyId]);

  // Load TimeTech projects for company (if any)
  useEffect(() => {
    async function loadProjects() {
      if (!isOpen || !initialTicket?.companyId) {
        setProjects([]);
        setSelectedProject(null);
        return;
      }

      setLoadingProjects(true);
      try {
        const projectList = await getTimeTechProjectsForCompany(initialTicket.companyId);
        // Only active projects
        const activeProjects = projectList.filter(p => p.isActive);
        setProjects(activeProjects);
        // Auto-select if only one project
        if (activeProjects.length === 1) {
          setSelectedProject(activeProjects[0]);
        } else {
          setSelectedProject(null);
        }
      } catch (err) {
        console.error('Failed to load TimeTech projects:', err);
        setProjects([]);
        setSelectedProject(null);
      } finally {
        setLoadingProjects(false);
      }
    }
    loadProjects();
  }, [isOpen, initialTicket?.companyId]);

  // Load bid-jobs for company
  useEffect(() => {
    async function loadBidJobs() {
      if (!isOpen || !initialTicket?.companyId) {
        setBidJobs([]);
        setSelectedBidJob(null);
        return;
      }

      setLoadingBidJobs(true);
      try {
        const bidJobList = await getBidJobsForCompany(initialTicket.companyId);
        setBidJobs(bidJobList);
        // Auto-select the default bid-job
        const defaultBidJob = getDefaultBidJob(bidJobList, initialTicket.companyId, myResourceId || undefined);
        setSelectedBidJob(defaultBidJob);
      } catch (err) {
        console.error('Failed to load bid-jobs:', err);
        setBidJobs([]);
        setSelectedBidJob(null);
      } finally {
        setLoadingBidJobs(false);
      }
    }
    loadBidJobs();
  }, [isOpen, initialTicket?.companyId]);

  // Auto-select TimeTech project based on bid-job type
  // PCP bid-jobs -> "Proactive Checks" project, other bid-jobs -> "Help Desk" project
  useEffect(() => {
    if (!selectedBidJob || projects.length === 0) {
      return;
    }
    const matchingProject = getProjectForBidJob(selectedBidJob, projects);
    if (matchingProject) {
      setSelectedProject(matchingProject);
    }
  }, [selectedBidJob, projects]);

  // Fetch everything in parallel when modal opens - show cached data immediately
  useEffect(() => {
    if (!isOpen || !initialTicket) return;

    // Show cached data immediately
    setTicket(initialTicket);

    const ticketId = initialTicket.autoTaskId;
    if (!ticketId) return;

    // Only show refreshing indicator if ticket is unassigned (we want to check if someone assigned it)
    const isUnassigned = !initialTicket.assignedResourceId;
    if (isUnassigned) {
      setIsRefreshing(true);
    }
    setIsLoadingNotes(true);
    setIsLoadingTimeEntries(true);

    // Fetch ticket, notes, and time entries all in parallel
    // Each updates independently as it completes for maximum responsiveness
    refreshTicket(ticketId)
      .then(freshTicket => {
        setTicket(freshTicket);
        // If status changed (e.g., ticket was completed), refresh the list immediately
        if (freshTicket.status !== initialTicket.status) {
          console.log(`Ticket status changed: ${initialTicket.status} -> ${freshTicket.status}, refreshing list`);
          onTicketUpdated?.();
        }
      })
      .catch(err => {
        console.error('Failed to refresh ticket:', err);
        // Show error to user if ticket not found in AutoTask
        const errMsg = err instanceof Error ? err.message : String(err);
        if (errMsg.includes('No matching records found') || errMsg.includes('404')) {
          setError(errMsg);
        }
      })
      .finally(() => setIsRefreshing(false));

    getTicketNotes(ticketId)
      .then(notes => setExistingNotes(notes))
      .catch(err => console.error('Failed to load notes:', err))
      .finally(() => setIsLoadingNotes(false));

    getTicketTimeEntries(ticketId)
      .then(entries => setExistingTimeEntries(entries))
      .catch(err => console.error('Failed to load time entries:', err))
      .finally(() => setIsLoadingTimeEntries(false));
  }, [isOpen, initialTicket?.autoTaskId]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Reset form state when ticket changes
  useEffect(() => {
    setNoteText('');
    setTimeNotes('');
    setHoursWorked('');
    setCloseAfterTime(false);
    setDateWorked(new Date().toISOString().split('T')[0]);
    setError(null);
    setSuccess(null);
    setActiveTab('details');
    setExistingNotes([]);
    setExistingTimeEntries([]);
    setPendingCompletion(false);
    setShowMergeDropdown(false);
    setMergeTickets([]);
    setProjects([]);
    setSelectedProject(null);
    setBidJobs([]);
    setSelectedBidJob(null);
    // Reset intake state
    setIntakeSessions([]);
    setIntakeEndUserName('');
    setIntakeEndUserEmail('');
    setExpandedIntakeId(null);
    setIntakeScanData({});
    // Reset diagnostic state
    setDiagnosticsExpanded(false);
    setDiagnosticsError(null);
    setTamCustomer(null);
    setTamDevices([]);
    setSelectedDevice(null);
    setDiagnosticTechNotes('');
    setDiagnosticSession(null);
    setExistingSessions([]);
    setPollingSession(false);
    // Reset email viewer
    setShowOriginalEmail(false);
    setOriginalEmail(null);
    setEmailError(null);
    setModalExpanded(false);
  }, [initialTicket?.id]);

  // Load devices when diagnostics section is expanded
  useEffect(() => {
    async function loadDevicesAndSessions() {
      if (!diagnosticsExpanded || !ticket?.companyId) return;

      setDiagnosticsLoading(true);
      setDiagnosticsError(null);

      try {
        // Load devices and existing sessions in parallel
        const [devicesResult, sessions] = await Promise.all([
          getDiagnosticDevices(ticket.companyId).catch(err => {
            console.error('Failed to load devices:', err);
            return null;
          }),
          getDiagnosticSessionsForTicket(ticket.autoTaskId).catch(err => {
            console.error('Failed to load sessions:', err);
            return [];
          }),
        ]);

        if (devicesResult) {
          setTamCustomer(devicesResult.customer);
          // Sort devices: online first, then by hostname
          const sortedDevices = [...devicesResult.devices].sort((a, b) => {
            if (a.status === 'online' && b.status !== 'online') return -1;
            if (a.status !== 'online' && b.status === 'online') return 1;
            return a.hostname.localeCompare(b.hostname);
          });
          setTamDevices(sortedDevices);
        } else {
          setDiagnosticsError('Customer not found in UMT-TAM or devices unavailable');
        }

        setExistingSessions(sessions);

        // If there's an active session, set it
        const activeSession = sessions.find(s =>
          ['pending', 'collecting', 'analyzing', 'ready'].includes(s.status)
        );
        if (activeSession) {
          setDiagnosticSession(activeSession);
        }
      } catch (err) {
        setDiagnosticsError(err instanceof Error ? err.message : 'Failed to load diagnostic data');
      } finally {
        setDiagnosticsLoading(false);
      }
    }

    loadDevicesAndSessions();
  }, [diagnosticsExpanded, ticket?.companyId, ticket?.autoTaskId]);

  // Poll for session status updates
  useEffect(() => {
    if (!diagnosticSession || !pollingSession) return;

    // Don't poll if session is in a terminal state
    const terminalStates: DiagnosticSessionStatus[] = ['ready', 'completed', 'expired', 'failed'];
    if (terminalStates.includes(diagnosticSession.status)) {
      setPollingSession(false);
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const updated = await getDiagnosticSession(diagnosticSession.id);
        setDiagnosticSession(updated);

        if (terminalStates.includes(updated.status)) {
          setPollingSession(false);
        }
      } catch (err) {
        console.error('Error polling session:', err);
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [diagnosticSession?.id, diagnosticSession?.status, pollingSession]);

  // Load intake sessions and contacts when tab is selected
  useEffect(() => {
    if (activeTab !== 'intake' || !ticket?.autoTaskId) return;

    setIsLoadingIntake(true);

    // Load ScreenConnect base URL setting
    getSystemSetting('screenConnectBaseUrl').then(url => {
      if (url) setScreenConnectBaseUrl(url as string);
    }).catch(() => {});

    // Load company contacts for the dropdown
    if (ticket.companyId) {
      setLoadingContacts(true);
      getCompanyContacts(ticket.companyId, true)
        .then(contacts => {
          setCompanyContacts(contacts);
          // Auto-select matching contact if ticket has contactId
          if (ticket.contactId) {
            const match = contacts.find(c => c.id === ticket.contactId);
            if (match) {
              setSelectedContactId(match.id);
              setIntakeEndUserName(`${match.firstName} ${match.lastName}`);
              setIntakeEndUserEmail(match.email);
            }
          }
          // If no contactId but ticket has contact info, pre-fill and try to match
          if (!ticket.contactId && ticket.contactName) {
            setIntakeEndUserName(ticket.contactName);
            if (ticket.contactEmail) {
              setIntakeEndUserEmail(ticket.contactEmail);
              const match = contacts.find(c => c.email.toLowerCase() === ticket.contactEmail!.toLowerCase());
              if (match) setSelectedContactId(match.id);
            }
          }
        })
        .catch(err => {
          console.error('Failed to load contacts:', err);
          // Fall back to ticket contact info
          if (ticket.contactName) setIntakeEndUserName(ticket.contactName);
          if (ticket.contactEmail) setIntakeEndUserEmail(ticket.contactEmail);
        })
        .finally(() => setLoadingContacts(false));
    } else {
      // No companyId — fall back to ticket contact info
      if (!intakeEndUserName && ticket.contactName) setIntakeEndUserName(ticket.contactName);
      if (!intakeEndUserEmail && ticket.contactEmail) setIntakeEndUserEmail(ticket.contactEmail);
    }

    getIntakeSessionsForTicket(ticket.autoTaskId)
      .then(sessions => setIntakeSessions(sessions))
      .catch(err => console.error('Failed to load intake sessions:', err))
      .finally(() => setIsLoadingIntake(false));
  }, [activeTab, ticket?.autoTaskId]);

  // Handle sending intake form
  const handleSendIntakeForm = useCallback(async () => {
    if (!ticket || !intakeEndUserName.trim() || !intakeEndUserEmail.trim()) return;

    setIsSendingIntake(true);
    setError(null);

    try {
      await createIntakeSession({
        ticketId: ticket.autoTaskId,
        ticketNumber: ticket.ticketNumber,
        companyId: ticket.companyId || 0,
        companyName: ticket.companyName || '',
        endUserName: intakeEndUserName.trim(),
        endUserEmail: intakeEndUserEmail.trim(),
        contactId: selectedContactId || ticket.contactId,
      });
      setSuccess(`Intake form sent to ${intakeEndUserEmail}`);
      // Reload sessions
      const sessions = await getIntakeSessionsForTicket(ticket.autoTaskId);
      setIntakeSessions(sessions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send intake form');
    } finally {
      setIsSendingIntake(false);
    }
  }, [ticket, intakeEndUserName, intakeEndUserEmail]);

  // Handle invalidating an intake session
  const handleInvalidateIntake = useCallback(async (sessionId: string) => {
    if (!ticket) return;
    try {
      await manageIntakeSession(sessionId, 'invalidate');
      const sessions = await getIntakeSessionsForTicket(ticket.autoTaskId);
      setIntakeSessions(sessions);
      setSuccess('Session invalidated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invalidate session');
    }
  }, [ticket]);

  // Load scan data for a device
  const loadScanData = useCallback(async (deviceId: number) => {
    if (intakeScanData[deviceId] !== undefined) return; // Already loaded
    try {
      const data = await getDeviceScanData(deviceId);
      setIntakeScanData(prev => ({ ...prev, [deviceId]: data }));
    } catch {
      setIntakeScanData(prev => ({ ...prev, [deviceId]: null }));
    }
  }, [intakeScanData]);

  // Handle creating a diagnostic session
  const handleCreateDiagnosticSession = useCallback(async () => {
    if (!ticket || !selectedDevice || !tamCustomer) return;

    setCreatingSession(true);
    setDiagnosticsError(null);

    try {
      const result = await createDiagnosticSession({
        ticketId: ticket.autoTaskId,
        ticketNumber: ticket.ticketNumber,
        autotaskCompanyId: String(ticket.companyId),
        companyName: ticket.companyName || 'Unknown',
        deviceUid: selectedDevice.uid,
        deviceHostname: selectedDevice.hostname,
        dattoSiteUid: tamCustomer.dattoSiteUid || '',
        ticketDescription: ticket.description || ticket.title,
        techNotes: diagnosticTechNotes,
        triggerType: 'urgent',
      });

      // Fetch the full session
      const session = await getDiagnosticSession(result.sessionId);
      setDiagnosticSession(session);
      setPollingSession(true);

      // Add to existing sessions
      setExistingSessions(prev => [session, ...prev]);
    } catch (err) {
      setDiagnosticsError(err instanceof Error ? err.message : 'Failed to create diagnostic session');
    } finally {
      setCreatingSession(false);
    }
  }, [ticket, selectedDevice, tamCustomer, diagnosticTechNotes]);

  // Load tickets for merge dropdown when opened
  const loadMergeTickets = async () => {
    if (!ticket?.companyId || !ticket?.autoTaskId) return;

    setIsLoadingMergeTickets(true);
    try {
      const tickets = await getCompanyTicketsForMerge(ticket.companyId, ticket.autoTaskId);
      setMergeTickets(tickets);
    } catch (err) {
      console.error('Failed to load merge tickets:', err);
      setMergeTickets([]);
    } finally {
      setIsLoadingMergeTickets(false);
    }
  };

  const handleMergeClick = () => {
    if (!showMergeDropdown) {
      loadMergeTickets();
      // Reset manual search state when opening
      setManualTicketNumber('');
      setManualTicketError(null);
      setFoundManualTicket(null);
    }
    setShowMergeDropdown(!showMergeDropdown);
  };

  const handleManualTicketSearch = async () => {
    if (!manualTicketNumber.trim()) return;

    setManualTicketSearching(true);
    setManualTicketError(null);
    setFoundManualTicket(null);

    try {
      const found = await getTicketByNumber(manualTicketNumber.trim());
      if (found) {
        // Don't allow merging the same ticket
        if (found.autoTaskId === ticket?.autoTaskId) {
          setManualTicketError('Cannot merge a ticket with itself');
        } else if (found.status === 'Complete' || found.status === 'Closed') {
          setManualTicketError(`Ticket ${found.ticketNumber} is already ${found.status}`);
        } else {
          setFoundManualTicket(found);
        }
      } else {
        setManualTicketError(`Ticket "${manualTicketNumber}" not found`);
      }
    } catch (err) {
      setManualTicketError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setManualTicketSearching(false);
    }
  };

  const handleMergeTicket = async (mergeTicketId: number, mergeTicketNumber: string) => {
    if (!ticket?.autoTaskId) return;

    if (!confirm(`Merge ticket ${mergeTicketNumber} into this ticket?\n\nThis will:\n- Add notes to both tickets\n- Mark ${mergeTicketNumber} as Complete\n- Copy its description to this ticket`)) {
      return;
    }

    setIsMerging(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await mergeTicket(ticket.autoTaskId, mergeTicketId);
      setSuccess(result.message);
      setShowMergeDropdown(false);
      // Refresh to show the new note
      await refreshAfterAction(true, false);
      onTicketUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to merge tickets');
    } finally {
      setIsMerging(false);
    }
  };

  const handleSnooze = async (days: 3 | 7 | 14 | 30) => {
    if (!ticket?.autoTaskId || !myResourceId) {
      setError('Please select a technician first');
      return;
    }

    setIsSnoozing(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await snoozeTicket(ticket.autoTaskId, days, myResourceId);
      setSuccess(`Snoozed for ${days} days (until ${new Date(result.snoozedUntil).toLocaleDateString()})`);
      // Refresh the ticket to show updated staleTracking
      await refreshAfterAction();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to snooze ticket');
    } finally {
      setIsSnoozing(false);
    }
  };

  const handleSetAutoClose = async (daysFromNow: number | null, isoDate?: string) => {
    if (!ticket?.autoTaskId) return;
    setIsAutoClosing(true);
    setError(null);
    setSuccess(null);
    try {
      let dateIso = isoDate;
      if (!dateIso && daysFromNow !== null) {
        const d = new Date();
        d.setDate(d.getDate() + daysFromNow);
        dateIso = d.toISOString().slice(0, 10);
      }
      await setTicketAutoClose(ticket.autoTaskId, {
        autoCloseAfterDate: dateIso,
        autoCloseWarnDays: 3,
      });
      setSuccess(`Auto-close set for ${dateIso}`);
      setShowAutoCloseDate(false);
      setAutoCloseDateInput('');
      await refreshAfterAction();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set auto-close');
    } finally {
      setIsAutoClosing(false);
    }
  };

  const handleCancelAutoClose = async () => {
    if (!ticket?.autoTaskId) return;
    setIsAutoClosing(true);
    setError(null);
    setSuccess(null);
    try {
      await setTicketAutoClose(ticket.autoTaskId, {
        autoCloseAfterDate: null,
        autoCloseIfInactiveDays: null,
      }, 'Cancelled by tech');
      setSuccess('Auto-close cancelled');
      await refreshAfterAction();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel auto-close');
    } finally {
      setIsAutoClosing(false);
    }
  };

  const handleMarkAsSpam = async () => {
    if (!ticket?.autoTaskId) return;

    if (!confirm('Mark this ticket as spam?\n\nThis will:\n- Add an internal note "Marked as spam - auto-closed"\n- Create a 0-hour time entry\n- Close the ticket')) {
      return;
    }

    setIsMarkingSpam(true);
    setError(null);
    setSuccess(null);

    try {
      await markTicketAsSpam(ticket.autoTaskId);
      setSuccess('Ticket marked as spam and closed');
      // Trigger optimistic removal from table
      if (ticket.id) {
        onTicketCompleted?.(ticket.id);
      }
      // Close modal after short delay
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark ticket as spam');
    } finally {
      setIsMarkingSpam(false);
    }
  };

  const handleNudge = async () => {
    if (!ticket?.autoTaskId || !myResourceId) {
      setError('Please select a technician first');
      return;
    }

    const statusNote = ticket.status !== 'Waiting Customer'
      ? `\n- Change status to "Waiting Customer"`
      : '';

    if (!confirm(`Send nudge to customer?\n\nThis will:\n- Post a customer-visible follow-up note${statusNote}\n- Start the 5-day auto-close countdown`)) {
      return;
    }

    setIsNudging(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await nudgeTicket(ticket.autoTaskId, myResourceId);
      setSuccess(`Customer nudged on ${result.ticketNumber}`);
      // Refresh the ticket to show updated status and tracking
      await refreshAfterAction(true, false);
      onTicketUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to nudge customer');
    } finally {
      setIsNudging(false);
    }
  };

  async function handleViewOriginalEmail(e?: React.MouseEvent) {
    e?.stopPropagation();
    if (!ticket?.autoTaskId) return;
    if (originalEmail) {
      setShowOriginalEmail(!showOriginalEmail);
      return;
    }
    setLoadingEmail(true);
    setEmailError(null);
    try {
      const email = await getTicketOriginalEmail(ticket.autoTaskId);
      setOriginalEmail(email);
      setShowOriginalEmail(true);
    } catch (err) {
      console.error('Failed to fetch original email:', err);
      setEmailError(err instanceof Error ? err.message : 'Failed to fetch original email');
    } finally {
      setLoadingEmail(false);
    }
  }

  function downloadAttachment(att: FCREmailAttachment) {
    if (!att.contentBytes) return;
    const byteChars = atob(att.contentBytes);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: att.contentType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = att.name;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Seed the editable caller fields from what the caller told Yumi, so the
  // tech corrects rather than retypes. Keyed on the ticket so switching
  // tickets in the modal doesn't carry one caller's details onto another's.
  //
  // MUST stay above the `if (!isOpen || !ticket) return null` below. Hooks run
  // unconditionally or not at all — placing this after the early return made
  // the hook count differ between the closed and open renders, which is React
  // error #310 and blanked the whole modal in v1.67.0.
  useEffect(() => {
    const c = ticket?.unverifiedCaller;
    setCallerFirst(c?.firstName || '');
    setCallerLast(c?.lastName || '');
    setCallerEmail(c?.email || '');
  }, [ticket?.autoTaskId, ticket?.unverifiedCaller]);

  if (!isOpen || !ticket) return null;

  const handleResourceChange = (resourceId: number) => {
    setSelectedResourceId(resourceId);
    // Do NOT write to localStorage here — this is for reassignment only,
    // not the logged-in user's identity (prevents cross-user contamination)
  };

  const getSelectedResource = () => resources.find(r => r.autoTaskId === selectedResourceId);
  const getMyResource = () => resources.find(r => r.autoTaskId === myResourceId);
  // Let the API look up the correct role if resource has no defaultRoleId
  const getMyRoleId = () => getMyResource()?.defaultRoleId;

  const refreshAfterAction = async (refreshNotes = false, refreshTimeEntries = false, skipTableRefresh = false) => {
    if (!ticket?.autoTaskId) return;
    try {
      const promises: Promise<any>[] = [refreshTicket(ticket.autoTaskId)];
      if (refreshNotes) promises.push(getTicketNotes(ticket.autoTaskId));
      if (refreshTimeEntries) promises.push(getTicketTimeEntries(ticket.autoTaskId));

      const results = await Promise.all(promises);
      setTicket(results[0]);
      if (refreshNotes && results[1]) setExistingNotes(results[1]);
      if (refreshTimeEntries && results[refreshNotes ? 2 : 1]) setExistingTimeEntries(results[refreshNotes ? 2 : 1]);
    } catch (err) {
      console.error('Failed to refresh ticket after action:', err);
    }
    // Skip table refresh when completing - the optimistic update already removed it
    if (!skipTableRefresh) {
      onTicketUpdated?.();
    }
  };

  // v1.67.0: a ticket opened from a call we couldn't identify carries
  // unverifiedCaller — details a voice supplied, nobody checked. Assign is
  // blocked until a tech either confirms them (creating the AutoTask contact)
  // or clears them (declining). Without this the details rot on the ticket and
  // the person never becomes a contact, which is how we got here.
  const needsCallerVerification = Boolean(
    !ticket?.contactId &&
    ticket?.unverifiedCaller &&
    (ticket.unverifiedCaller.firstName?.trim() ||
      ticket.unverifiedCaller.lastName?.trim() ||
      ticket.unverifiedCaller.email?.trim()),
  );

  /**
   * Confirm: create the AutoTask contact and attach it. Reuses the same
   * create-and-attach path the Intake tab already uses. Once contactId is set,
   * needsCallerVerification goes false and Assign unlocks.
   */
  const handleConfirmCaller = async () => {
    if (!ticket?.companyId) {
      setError('This ticket has no company, so a contact cannot be created.');
      return;
    }
    setIsConfirmingCaller(true);
    setError(null);
    try {
      const created = await createContact({
        companyId: ticket.companyId,
        firstName: callerFirst.trim(),
        lastName: callerLast.trim(),
        email: callerEmail.trim(),
      });
      await updateTicket(ticket.autoTaskId, { contactID: created.id, unverifiedCaller: null });
      ticket.contactId = created.id;
      ticket.contactName = `${created.firstName} ${created.lastName}`;
      ticket.contactEmail = created.email;
      ticket.unverifiedCaller = undefined;
      onTicketUpdated?.();
      setSuccess('Contact created and attached — you can assign now');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contact');
    } finally {
      setIsConfirmingCaller(false);
    }
  };

  /**
   * Decline: clear the details. That IS the whole gesture — no reason, no
   * note, nothing recorded (jgoode 2026-08-17). Used when this caller
   * shouldn't become a contact at all.
   */
  const handleDismissCaller = async () => {
    setIsConfirmingCaller(true);
    setError(null);
    try {
      await updateTicket(ticket.autoTaskId, { unverifiedCaller: null });
      ticket.unverifiedCaller = undefined;
      setCallerFirst('');
      setCallerLast('');
      setCallerEmail('');
      onTicketUpdated?.();
      setSuccess('Caller details cleared');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear caller details');
    } finally {
      setIsConfirmingCaller(false);
    }
  };

  const handleAssign = async () => {
    if (needsCallerVerification) {
      setError('Confirm or clear the caller details before assigning this ticket.');
      return;
    }
    if (!selectedResourceId) {
      setError('Please select a technician');
      return;
    }

    const targetResource = getSelectedResource();

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Build update payload - only include roleID if the resource has one configured
      const updatePayload: { assignedResourceID: number; assignedResourceRoleID?: number; status: number } = {
        assignedResourceID: selectedResourceId,
        status: 8, // In Progress
      };
      if (targetResource?.defaultRoleId) {
        updatePayload.assignedResourceRoleID = targetResource.defaultRoleId;
      }
      await updateTicket(ticket.autoTaskId, updatePayload);
      setSuccess('Ticket assigned!');
      await refreshAfterAction();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign ticket');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (statusId: number) => {
    // If trying to complete, require time entry first
    if (statusId === 5) {
      setPendingCompletion(true);
      setActiveTab('time');
      setCloseAfterTime(true); // Pre-check the "complete after time" checkbox
      setSuccess('Please add time entry to complete this ticket');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await updateTicket(ticket.autoTaskId, { status: statusId });
      setSuccess('Status updated!');
      await refreshAfterAction(false, false, false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePriorityChange = async (priorityId: number) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await updateTicket(ticket.autoTaskId, { priority: priorityId });
      setSuccess('Priority updated!');
      await refreshAfterAction();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update priority');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) {
      setError('Please enter note text');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await addTicketNote(ticket.autoTaskId, {
        description: noteText,
        noteType: noteType,
        publish: 1,
        creatorResourceID: myResourceId || undefined,
      });
      setSuccess('Note added!');
      setNoteText('');
      await refreshAfterAction(true, false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add note');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTimeEntry = async (andClose: boolean = false) => {
    const willClose = andClose || closeAfterTime;
    const hours = parseFloat(hoursWorked) || 0;

    if (!myResourceId) {
      setError('Could not determine your technician identity — please reload');
      return;
    }

    // Check if submitting mileage or expense only (no time entry needed)
    const hasMileage = showMileage && odometerStart && odometerEnd;
    const hasExpense = showExpense && expenseItem && parseFloat(expenseAmount || '0') > 0;
    const onlyMileageOrExpense = (hasMileage || hasExpense) && hours <= 0 && !timeNotes.trim();

    // Validate mileage fields if checked
    if (showMileage && (!odometerStart || !odometerEnd)) {
      setError('Please enter odometer start and end');
      return;
    }
    if (showMileage && parseFloat(odometerEnd) <= parseFloat(odometerStart)) {
      setError('Odometer end must be greater than start');
      return;
    }

    // Validate expense fields if checked
    if (showExpense && (!expenseItem || !expenseAmount || parseFloat(expenseAmount) <= 0)) {
      setError('Please enter expense item and amount');
      return;
    }

    // Allow 0 hours only when completing the ticket or submitting mileage/expense only
    if (hours <= 0 && !willClose && !onlyMileageOrExpense) {
      setError('Please enter hours worked');
      return;
    }
    if (!timeNotes.trim() && !onlyMileageOrExpense) {
      setError('Please enter work summary');
      return;
    }
    // Require project selection if company has projects
    if (projects.length > 0 && !selectedProject) {
      setError('Please select a project for this company');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Only create time entry if hours > 0
      if (hours > 0) {
        // Use selected bid-job, or fallback to Help Desk if available
        let bidJobToUse = selectedBidJob;
        if (!bidJobToUse && bidJobs.length > 0) {
          // Find Help Desk bid-job as fallback
          bidJobToUse = bidJobs.find(bj =>
            bj.bidJobDescription?.toLowerCase().includes('help desk')
          ) || null;
        }

        // Use selected project, or find Help Desk project if bid-job is Help Desk type
        let projectToUse = selectedProject;
        if (!projectToUse && projects.length > 0 && bidJobToUse) {
          // Find matching project based on bid-job type
          const isPCP = bidJobToUse.bidJobNumber?.toUpperCase().startsWith('PCP');
          const targetProjectName = isPCP ? 'Proactive Checks' : 'Help Desk';
          projectToUse = projects.find(p =>
            p.projectName.toLowerCase() === targetProjectName.toLowerCase() && p.isActive
          ) || null;
        }

        const result = await addTimeEntry(ticket.autoTaskId, {
          resourceID: myResourceId,
          roleID: getMyRoleId(),
          hoursWorked: hours,
          summaryNotes: timeNotes,
          dateWorked: dateWorked,
          // Pass ticket info explicitly to ensure Cosmos entry has correct data
          companyId: ticket.companyId,
          companyName: ticket.companyName,
          ticketNumber: ticket.ticketNumber,
          ticketTitle: ticket.title,
          // Include project (selected or fallback)
          projectId: projectToUse?.projectId,
          projectName: projectToUse?.projectName,
          // Include bid-job (selected or fallback)
          bidJobNumber: bidJobToUse?.bidJobNumber,
          bidJobDescription: bidJobToUse?.bidJobDescription,
        });

        // Warn if TimeTech sync failed
        if (result.timetech && !result.timetech.success) {
          addToast('warning', `Time saved to AutoTask but TimeTech sync failed: ${result.timetech.error}`);
        }
        // Warn if Cosmos write failed (entry won't appear on calendar)
        if (result.cosmos && !result.cosmos.success) {
          console.warn('Time entry Cosmos write failed:', result.cosmos.error);
        }
      }

      // Save mileage entry if checked
      if (showMileage && odometerStart && odometerEnd) {
        const myResource = resources.find(r => r.autoTaskId === myResourceId);
        await createMileageEntry({
          resourceId: myResourceId,
          resourceName: myResource ? `${myResource.firstName} ${myResource.lastName}` : '',
          dateWorked: dateWorked,
          startLocation: mileageStart,
          destination: mileageDestination || ticket.companyName || '',
          destinationCompanyId: ticket.companyId,
          estimatedMiles: estimatedMiles || undefined,
          odometerStart: parseFloat(odometerStart),
          odometerEnd: parseFloat(odometerEnd),
          ratePerMile: mileageRate,
          item: mileageItem || `Drive to ${mileageDestination || ticket.companyName || 'client'}`,
          ticketId: ticket.autoTaskId,
          ticketNumber: ticket.ticketNumber,
        });
      }

      // Save expense entry if checked
      if (showExpense && expenseItem && expenseAmount) {
        const myResource = resources.find(r => r.autoTaskId === myResourceId);
        await createExpenseEntry({
          resourceId: myResourceId,
          resourceName: myResource ? `${myResource.firstName} ${myResource.lastName}` : '',
          dateWorked: dateWorked,
          item: expenseItem,
          amount: parseFloat(expenseAmount),
          companyId: ticket.companyId,
          companyName: ticket.companyName,
          ticketId: ticket.autoTaskId,
          ticketNumber: ticket.ticketNumber,
        });
      }

      if (willClose) {
        // If ticket is unassigned, assign it to the tech first before closing
        if (!ticket.assignedResourceId && myResourceId) {
          const roleId = getMyRoleId();
          await updateTicket(ticket.autoTaskId, {
            assignedResourceID: myResourceId,
            ...(roleId ? { assignedResourceRoleID: roleId } : {}),
          });
        }
        // Post customer-visible note with work summary so AutoTask emails the customer
        await addTicketNote(ticket.autoTaskId, {
          description: timeNotes || 'Completed',
          noteType: 2,  // Customer Visible
          publish: 1,   // All Users
          creatorResourceID: myResourceId || undefined,
        });
        await updateTicket(ticket.autoTaskId, { status: 5, resolution: timeNotes || 'Completed' });
        setSuccess(hours > 0 ? 'Time added and ticket completed!' : 'Ticket completed!');
        // Trigger optimistic removal from table
        if (ticket.id) {
          onTicketCompleted?.(ticket.id);
        }
        // Skip table refresh - optimistic update already handled it
        await refreshAfterAction(false, true, true);
        setTimeout(onClose, 1500);
      } else {
        const parts: string[] = [];
        if (hours > 0) parts.push('Time entry');
        if (showMileage && odometerStart && odometerEnd) parts.push('Mileage');
        if (showExpense && expenseItem) parts.push('Expense');
        setSuccess(`${parts.join(' + ') || 'Entry'} added!`);
        setHoursWorked('');
        setTimeNotes('');
        setShowMileage(false);
        setShowExpense(false);
        setOdometerStart('');
        setOdometerEnd('');
        setMileageItem('');
        setEstimatedMiles(null);
        setExpenseItem('');
        setExpenseAmount('');
        await refreshAfterAction(false, true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add entry');
    } finally {
      setIsLoading(false);
    }
  };

  const getTicketDate = () => (ticket as any).createdDate || ticket.createDate;
  const isTicketClosed = ticket.status === 'Complete' || ticket.status === 'Closed';
  const isUnassigned = !ticket.assignedResourceId;

  // Get current status/priority IDs for selects - use ID fields directly when available
  const getCurrentStatusId = () => {
    if (ticket.statusId) return ticket.statusId;
    const found = statusOptions.find(s => s.name.toLowerCase() === ticket.status?.toLowerCase());
    return found?.id || 1;
  };

  const getCurrentPriorityId = () => {
    if (ticket.priorityId) return ticket.priorityId;
    const found = priorityOptions.find(p => p.name.toLowerCase() === ticket.priority?.toLowerCase());
    return found?.id || 3;
  };

  // Show loading animation for assigned field only when syncing AND currently unassigned
  const renderAssignedValue = () => {
    if (isRefreshing && isUnassigned) {
      return <LoadingDots />;
    }
    return (
      <span className={ticket.assignedResourceName ? 'text-green-400' : 'text-yellow-400'}>
        {ticket.assignedResourceName || 'Unassigned'}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal - fixed height, expandable */}
      <div className={`relative bg-slate-800 border border-slate-600 rounded-xl shadow-2xl w-full flex flex-col mx-4 transition-all duration-200 ${modalExpanded ? 'max-w-5xl h-[85vh]' : 'max-w-2xl h-[520px]'}`}>
        {/* Header - Compact: ticket#, title, close button */}
        <div className="flex items-center gap-3 px-4 py-2 border-b border-slate-700 bg-slate-800/80">
          <a
            href={`https://ww6.autotask.net/Mvc/ServiceDesk/TicketDetail.mvc?ticketId=${ticket.autoTaskId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-blue-400 hover:text-blue-300 hover:underline font-bold text-sm shrink-0"
            title="Open in AutoTask"
          >{ticket.ticketNumber}</a>
          <h2 className="text-sm font-medium text-white truncate flex-1" title={ticket.title}>{ticket.title}</h2>
          {/* Expand toggle — moved here from the Details tab so it's reachable
              from Notes / Time / any tab. Previously hidden when activeTab !== 'details',
              which is why jgoode's 6/17 feedback called out "can't see expand button"
              on the Notes tab. */}
          <button
            onClick={() => setModalExpanded(!modalExpanded)}
            className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-700 transition-colors shrink-0"
            title={modalExpanded ? 'Shrink modal' : 'Expand modal'}
          >
            {modalExpanded ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9L4 4m0 0v4m0-4h4m6 6l5 5m0 0v-4m0 4h-4" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            )}
          </button>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-700 transition-colors shrink-0"
            title="Close (Esc)"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Info bar - Client, Assigned, Status, Priority dropdowns */}
        <div className="flex items-center gap-4 px-4 py-2 border-b border-slate-700 text-xs bg-slate-800/50 flex-wrap">
          <span className="text-slate-400">Client: {ticket.companyName?.toLowerCase().includes('unknown') ? (
            showCompanyPicker ? (
              <span className="inline-flex items-center gap-1 relative">
                <input
                  type="text"
                  value={companySearch}
                  onChange={async (e) => {
                    setCompanySearch(e.target.value);
                    if (e.target.value.length >= 2 && companyOptions.length === 0) {
                      setLoadingCompanies(true);
                      try {
                        const companies = await getActiveCompanies();
                        setCompanyOptions(companies);
                      } catch { /* ignore */ }
                      setLoadingCompanies(false);
                    }
                  }}
                  placeholder="Search company..."
                  autoFocus
                  className="bg-slate-700 text-white text-xs px-2 py-0.5 rounded border border-slate-600 w-48 focus:outline-none focus:border-blue-500"
                />
                <button onClick={() => { setShowCompanyPicker(false); setCompanySearch(''); }} className="text-slate-500 hover:text-white text-xs">✕</button>
                {(companySearch.length >= 2 && companyOptions.length > 0) && (
                  <div className="absolute top-full left-0 mt-1 w-64 max-h-48 overflow-y-auto bg-slate-800 border border-slate-600 rounded shadow-lg z-50">
                    {companyOptions
                      .filter(c => c.companyName.toLowerCase().includes(companySearch.toLowerCase()))
                      .slice(0, 20)
                      .map(c => (
                        <button
                          key={c.id}
                          onClick={async () => {
                            setReassigningCompany(true);
                            try {
                              await updateTicket(ticket.autoTaskId, { companyID: c.autoTaskId });
                              setShowCompanyPicker(false);
                              setCompanySearch('');
                              await refreshAfterAction();
                            } catch (err) {
                              setError(`Failed to reassign company: ${err instanceof Error ? err.message : String(err)}`);
                            }
                            setReassigningCompany(false);
                          }}
                          disabled={reassigningCompany}
                          className="w-full text-left px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                        >
                          {c.companyName}
                        </button>
                      ))}
                    {companyOptions.filter(c => c.companyName.toLowerCase().includes(companySearch.toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-xs text-slate-500">No matches</div>
                    )}
                  </div>
                )}
                {loadingCompanies && <span className="text-xs text-slate-500">Loading...</span>}
              </span>
            ) : (
              <button
                onClick={() => setShowCompanyPicker(true)}
                className="text-amber-400 hover:text-amber-300 underline decoration-dotted cursor-pointer"
                title="Click to reassign to a real company"
              >
                {ticket.companyName}
              </button>
            )
          ) : (
            <span className="text-slate-300">{ticket.companyName || '-'}</span>
          )}</span>
          <span className="text-slate-400">Assigned: {renderAssignedValue()}</span>
          <span className="text-slate-400">Contact: {companyContacts.length > 0 || showInfoBarNewContact ? (
            showInfoBarNewContact ? (
              <span className="text-xs text-blue-400 cursor-pointer hover:text-blue-300" onClick={() => setShowInfoBarNewContact(false)}>Adding new...</span>
            ) : (
              <select
                value={selectedContactId || ''}
                onChange={async (e) => {
                  const val = e.target.value;
                  if (val === '__new__') {
                    setShowInfoBarNewContact(true);
                    setNewContactFirstName('');
                    setNewContactLastName('');
                    setNewContactEmail('');
                    return;
                  }
                  const newContactId = parseInt(val);
                  if (!newContactId || !ticket) return;
                  const prev = selectedContactId;
                  setSelectedContactId(newContactId);
                  try {
                    await updateTicket(ticket.autoTaskId, { contactID: newContactId });
                    const contact = companyContacts.find(c => c.id === newContactId);
                    if (contact) {
                      ticket.contactId = newContactId;
                      ticket.contactName = `${contact.firstName} ${contact.lastName}`;
                      ticket.contactEmail = contact.email;
                    }
                    onTicketUpdated?.();
                  } catch (err) {
                    setSelectedContactId(prev);
                    setError(err instanceof Error ? err.message : 'Failed to update contact');
                  }
                }}
                disabled={isLoading || isTicketClosed}
                className="text-xs text-slate-300 bg-transparent border-none cursor-pointer hover:text-white focus:outline-none appearance-none pr-3"
                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'8\' height=\'8\' viewBox=\'0 0 8 8\'%3E%3Cpath fill=\'%2394a3b8\' d=\'M0 2l4 4 4-4z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0 center' }}
              >
                <option value="" className="bg-slate-800">No contact</option>
                {companyContacts.map(c => (
                  <option key={c.id} value={c.id} className="bg-slate-800">
                    {c.firstName} {c.lastName} ({c.email})
                  </option>
                ))}
                {!isTicketClosed && <option value="__new__" className="bg-slate-800">+ Add new...</option>}
              </select>
            )
          ) : (
            <span className="text-slate-500">{ticket.contactEmail || ticket.contactName || '-'}</span>
          )}</span>
          <div className="flex items-center gap-1 ml-auto">
            <select
              value={getCurrentStatusId()}
              onChange={(e) => handleStatusChange(parseInt(e.target.value))}
              disabled={isLoading || isTicketClosed}
              className={`text-xs font-medium px-1.5 py-0.5 rounded border cursor-pointer bg-transparent ${getStatusColor(ticket.status)} disabled:opacity-50`}
            >
              {statusOptions.map(s => (
                <option key={s.id} value={s.id} className="bg-slate-800 text-white">{s.name}</option>
              ))}
            </select>
            <select
              value={getCurrentPriorityId()}
              onChange={(e) => handlePriorityChange(parseInt(e.target.value))}
              disabled={isLoading || isTicketClosed}
              className={`text-xs font-medium px-1.5 py-0.5 rounded border cursor-pointer bg-transparent ${getPriorityColor(ticket.priority)} disabled:opacity-50`}
            >
              {priorityOptions.map(p => (
                <option key={p.id} value={p.id} className="bg-slate-800 text-white">{p.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Inline new contact form (info bar) */}
        {showInfoBarNewContact && (
          <div className="px-4 py-2 bg-slate-800/80 border-b border-slate-700 shrink-0">
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                placeholder="First name"
                value={newContactFirstName}
                onChange={e => setNewContactFirstName(e.target.value)}
                className="bg-slate-700 text-white text-xs px-2 py-1 rounded border border-slate-600 focus:border-blue-500 outline-none w-28"
                autoFocus
              />
              <input
                type="text"
                placeholder="Last name"
                value={newContactLastName}
                onChange={e => setNewContactLastName(e.target.value)}
                className="bg-slate-700 text-white text-xs px-2 py-1 rounded border border-slate-600 focus:border-blue-500 outline-none w-28"
              />
              <input
                type="email"
                placeholder="Email"
                value={newContactEmail}
                onChange={e => setNewContactEmail(e.target.value)}
                className="bg-slate-700 text-white text-xs px-2 py-1 rounded border border-slate-600 focus:border-blue-500 outline-none w-48"
              />
              <button
                onClick={async () => {
                  if (!ticket?.companyId || !newContactFirstName.trim() || !newContactLastName.trim() || !newContactEmail.trim()) return;
                  setCreatingContact(true);
                  try {
                    const created = await createContact({
                      companyId: ticket.companyId,
                      firstName: newContactFirstName.trim(),
                      lastName: newContactLastName.trim(),
                      email: newContactEmail.trim(),
                    });
                    const refreshed = await getCompanyContacts(ticket.companyId, true);
                    setCompanyContacts(refreshed);
                    setSelectedContactId(created.id);
                    // Update ticket with the new contact
                    await updateTicket(ticket.autoTaskId, { contactID: created.id });
                    ticket.contactId = created.id;
                    ticket.contactName = `${created.firstName} ${created.lastName}`;
                    ticket.contactEmail = created.email;
                    onTicketUpdated?.();
                    setShowInfoBarNewContact(false);
                    setNewContactFirstName('');
                    setNewContactLastName('');
                    setNewContactEmail('');
                    setSuccess('Contact created and assigned to ticket');
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to create contact');
                  } finally {
                    setCreatingContact(false);
                  }
                }}
                disabled={creatingContact || !newContactFirstName.trim() || !newContactLastName.trim() || !newContactEmail.trim()}
                className="text-xs font-medium px-2 py-1 rounded bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white transition-colors"
              >
                {creatingContact ? 'Creating...' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setShowInfoBarNewContact(false);
                  setNewContactFirstName('');
                  setNewContactLastName('');
                  setNewContactEmail('');
                }}
                className="text-xs font-medium px-2 py-1 rounded bg-slate-600 hover:bg-slate-500 text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-slate-700 shrink-0">
          <button
            onClick={() => setActiveTab('details')}
            className={`px-4 py-2 text-xs font-medium transition-colors ${
              activeTab === 'details' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-slate-400 hover:text-white'
            }`}
          >
            Details
          </button>
          <button
            onClick={() => {
              setActiveTab('notes');
              // Auto-expand the modal on switching to Notes — the default
              // 520px height makes a long thread of notes unusable. Users
              // can still shrink with the header button. Per jgoode 6/17.
              setModalExpanded(true);
            }}
            className={`px-4 py-2 text-xs font-medium transition-colors ${
              activeTab === 'notes' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-slate-400 hover:text-white'
            }`}
          >
            Notes
          </button>
          <button
            onClick={() => setActiveTab('time')}
            className={`px-4 py-2 text-xs font-medium transition-colors ${
              activeTab === 'time' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-slate-400 hover:text-white'
            }`}
          >
            Add Time
          </button>
          <button
            onClick={() => setActiveTab('intake')}
            className={`px-4 py-2 text-xs font-medium transition-colors ${
              activeTab === 'intake' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-slate-400 hover:text-white'
            }`}
          >
            Intake
          </button>
        </div>

        {/* Content - Fixed height area */}
        <div className="flex-1 overflow-hidden px-4 py-3 flex flex-col">
          {/* Error/Success Messages */}
          {(error || success) && (
            <div className={`mb-2 p-2 rounded text-xs ${
              error ? 'bg-red-500/20 border border-red-500/30 text-red-400' : 'bg-green-500/20 border border-green-500/30 text-green-400'
            }`}>
              {error?.includes('No matching records found') ? (
                <div>
                  <strong>Ticket not found in AutoTask.</strong> This ticket may have been deleted, merged, or moved to a restricted queue.
                  The cached version is shown but cannot be updated. Try refreshing the ticket list.
                </div>
              ) : (
                error || success
              )}
            </div>
          )}

          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="flex flex-col flex-1 overflow-hidden space-y-2">
              {/* Project Status — only on tickets flagged isProject. Lets a tech
                  set % complete / health / status note + append updates. The
                  data is pulled by OverSite V3 via /api/tickets and projected
                  into its Project Management surface. */}
              {ticket.isProject && (
                <ProjectStatusPanel
                  ticket={ticket}
                  myResourceId={myResourceId ?? undefined}
                  onUpdated={(patch) => {
                    Object.assign(ticket, patch);
                    onTicketUpdated?.();
                  }}
                />
              )}

              {/* v1.67.0: unverified caller — sits directly beside Assign, not
                  on the Intake tab, so a tech blocked from assigning doesn't
                  have to go hunting for the reason (jgoode 2026-08-17). */}
              {!isTicketClosed && needsCallerVerification && (
                <div className="bg-amber-500/10 border border-amber-500/50 rounded p-2 shrink-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-amber-300 shrink-0">
                      Caller not verified
                    </span>
                    <input
                      value={callerFirst}
                      onChange={(e) => setCallerFirst(e.target.value)}
                      placeholder="First"
                      className="w-24 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs focus:border-blue-500"
                    />
                    <input
                      value={callerLast}
                      onChange={(e) => setCallerLast(e.target.value)}
                      placeholder="Last"
                      className="w-24 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs focus:border-blue-500"
                    />
                    <input
                      value={callerEmail}
                      onChange={(e) => setCallerEmail(e.target.value)}
                      placeholder="email@company.com"
                      className="w-52 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs focus:border-blue-500"
                    />
                    <button
                      onClick={handleConfirmCaller}
                      disabled={isConfirmingCaller || !callerFirst.trim() || !callerLast.trim() || !callerEmail.trim()}
                      className="bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white text-xs font-medium py-1 px-3 rounded transition-colors"
                    >
                      {isConfirmingCaller ? '...' : 'Confirm & create contact'}
                    </button>
                    <button
                      onClick={handleDismissCaller}
                      disabled={isConfirmingCaller}
                      title="Clear the details — use when this caller shouldn't become a contact"
                      className="text-xs text-slate-400 hover:text-slate-200 underline disabled:opacity-50"
                    >
                      Not a contact
                    </button>
                  </div>
                  <p className="text-[11px] text-amber-200/70 mt-1">
                    Given by phone, unchecked{ticket.unverifiedCaller?.confirmedAt ? ' — caller confirmed the spelling, which is not proof of identity' : ''}. Assign unlocks once this is resolved.
                  </p>
                </div>
              )}

              {/* Assign/Reassign Section */}
              {!isTicketClosed && (
                <div className="bg-slate-700/30 border border-slate-600 rounded p-2 shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 shrink-0">{isUnassigned ? 'Assign:' : 'Reassign:'}</span>
                    <select
                      value={selectedResourceId || ''}
                      onChange={(e) => handleResourceChange(parseInt(e.target.value))}
                      className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs focus:border-blue-500"
                    >
                      <option value="">Select tech...</option>
                      {resources.map((r) => (
                        <option key={r.autoTaskId} value={r.autoTaskId}>
                          {r.firstName} {r.lastName}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleAssign}
                      disabled={isLoading || !selectedResourceId || needsCallerVerification}
                      title={needsCallerVerification ? 'Confirm or clear the caller details first' : undefined}
                      className="bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white text-xs font-medium py-1 px-3 rounded transition-colors"
                    >
                      {isLoading ? '...' : isUnassigned ? 'Assign' : 'Reassign'}
                    </button>
                  </div>
                </div>
              )}

              {/* Quick Actions: Merge & Spam */}
              {!isTicketClosed && (
                <div className="relative shrink-0 flex items-center gap-2">
                  <button
                    onClick={handleMergeClick}
                    disabled={isMerging}
                    className="px-3 py-1.5 text-xs font-medium bg-purple-600/20 text-purple-300 border border-purple-500/50 rounded-md hover:bg-purple-600/30 hover:border-purple-400 disabled:opacity-50 flex items-center gap-1.5 transition-all"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    {isMerging ? 'Merging...' : 'Merge'}
                  </button>

                  {/* Project toggle */}
                  <button
                    onClick={async () => {
                      try {
                        const newVal = !ticket.isProject;
                        await toggleProjectFlag(ticket.autoTaskId, newVal);
                        ticket.isProject = newVal;
                        addToast('success', newVal ? 'Marked as project' : 'Removed project flag');
                        onTicketUpdated?.();
                      } catch (err) {
                        addToast('error', err instanceof Error ? err.message : 'Failed to toggle project flag');
                      }
                    }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5 transition-all ${
                      ticket.isProject
                        ? 'bg-teal-600/30 text-teal-300 border border-teal-500/50 hover:bg-teal-600/40'
                        : 'bg-slate-600/20 text-slate-400 border border-slate-500/50 hover:bg-slate-600/30 hover:text-slate-300'
                    }`}
                    title={ticket.isProject ? 'Remove project flag' : 'Mark as long-running project'}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    {ticket.isProject ? 'Project' : 'Project'}
                  </button>

                  {/* Mark as Spam - only show for New or In Progress tickets */}
                  {(ticket.status === 'New' || ticket.status === 'In Progress') && (
                    <button
                      onClick={handleMarkAsSpam}
                      disabled={isMarkingSpam}
                      className="px-3 py-1.5 text-xs font-medium bg-red-600/20 text-red-300 border border-red-500/50 rounded-md hover:bg-red-600/30 hover:border-red-400 disabled:opacity-50 flex items-center gap-1.5 transition-all"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                      {isMarkingSpam ? 'Processing...' : 'Spam'}
                    </button>
                  )}

                  {/* Nudge Customer - available for non-closed tickets */}
                  <button
                    onClick={handleNudge}
                    disabled={isNudging || !myResourceId}
                    className="px-3 py-1.5 text-xs font-medium bg-amber-600/20 text-amber-300 border border-amber-500/50 rounded-md hover:bg-amber-600/30 hover:border-amber-400 disabled:opacity-50 flex items-center gap-1.5 transition-all"
                    title={!myResourceId ? 'Select a technician first' : 'Send follow-up to customer'}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {isNudging ? 'Sending...' : 'Nudge'}
                  </button>

                  {/* View Original Email - for FCR-sourced tickets */}
                  {(ticket.fcrId || (ticket.description && ticket.description.includes('Forwarded by:') && ticket.description.includes('--- Original Message ---'))) && (
                    <button
                      onClick={(e) => handleViewOriginalEmail(e)}
                      disabled={loadingEmail}
                      className="px-3 py-1.5 text-xs font-medium bg-violet-600/20 text-violet-300 border border-violet-500/50 rounded-md hover:bg-violet-600/30 hover:border-violet-400 disabled:opacity-50 flex items-center gap-1.5 transition-all"
                    >
                      {loadingEmail ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-violet-300"></div>
                      ) : (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      )}
                      {showOriginalEmail ? 'Hide Email' : 'Original Email'}
                    </button>
                  )}

                  {/* Merge Dropdown */}
                  {showMergeDropdown && (
                    <div className="absolute left-0 top-6 z-50 bg-slate-800 border border-slate-600 rounded-lg shadow-xl min-w-[300px] max-w-[400px]">
                      {/* Manual ticket number search */}
                      <div className="p-2 border-b border-slate-700">
                        <label className="text-xs text-slate-400 block mb-1">Enter ticket number:</label>
                        <div className="flex gap-1">
                          <input
                            type="text"
                            value={manualTicketNumber}
                            onChange={(e) => setManualTicketNumber(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleManualTicketSearch()}
                            placeholder="T20260120.0024"
                            className="flex-1 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                          />
                          <button
                            onClick={handleManualTicketSearch}
                            disabled={manualTicketSearching || !manualTicketNumber.trim()}
                            className="bg-purple-600 hover:bg-purple-700 disabled:bg-slate-600 text-white text-xs px-2 py-1 rounded"
                          >
                            {manualTicketSearching ? '...' : 'Find'}
                          </button>
                        </div>
                        {manualTicketError && (
                          <div className="text-red-400 text-xs mt-1">{manualTicketError}</div>
                        )}
                        {foundManualTicket && (
                          <button
                            onClick={() => handleMergeTicket(foundManualTicket.autoTaskId, foundManualTicket.ticketNumber)}
                            disabled={isMerging}
                            className="w-full mt-2 text-left p-2 bg-purple-600/20 border border-purple-500/30 hover:bg-purple-600/30 rounded text-xs transition-colors disabled:opacity-50"
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-purple-400">{foundManualTicket.ticketNumber}</span>
                              <span className="text-slate-400">({foundManualTicket.companyName})</span>
                            </div>
                            <div className="text-slate-300 truncate mt-0.5">{foundManualTicket.title}</div>
                            <div className="text-purple-400 text-[10px] mt-1">Click to merge this ticket</div>
                          </button>
                        )}
                      </div>

                      <div className="p-2 border-b border-slate-700">
                        <span className="text-xs text-slate-400">Or select from same client:</span>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {isLoadingMergeTickets ? (
                          <div className="p-3 text-center text-slate-400 text-xs">Loading tickets<LoadingDots /></div>
                        ) : mergeTickets.length === 0 ? (
                          <div className="p-3 text-center text-slate-500 text-xs">No other open tickets for this client</div>
                        ) : (
                          <div className="p-1">
                            {mergeTickets.map((t) => (
                              <button
                                key={t.autoTaskId}
                                onClick={() => handleMergeTicket(t.autoTaskId, t.ticketNumber)}
                                disabled={isMerging}
                                className="w-full text-left px-2 py-1.5 hover:bg-slate-700 rounded text-xs transition-colors disabled:opacity-50"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-blue-400">{t.ticketNumber}</span>
                                  <span className={`px-1 rounded text-[10px] ${
                                    t.status === 'New' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'
                                  }`}>
                                    {t.status}
                                  </span>
                                </div>
                                <div className="text-slate-400 truncate mt-0.5">{t.title}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="p-2 border-t border-slate-700">
                        <button
                          onClick={() => setShowMergeDropdown(false)}
                          className="text-xs text-slate-400 hover:text-white"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Stale Ticket Management */}
              {!isTicketClosed && (ticket.status === 'Waiting Customer' || ticket.status === 'Waiting On Us') && (
                <div className="bg-slate-700/30 border border-slate-600 rounded p-2 shrink-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-slate-400">Stale Tracking</span>
                    {ticket.staleTracking?.snoozedUntil && new Date(ticket.staleTracking.snoozedUntil) > new Date() && (
                      <span className="text-[10px] bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 px-1.5 py-0.5 rounded">
                        Snoozed until {new Date(ticket.staleTracking.snoozedUntil).toLocaleDateString()}
                      </span>
                    )}
                    {ticket.staleTracking?.nudgedAt && !ticket.staleTracking?.snoozedUntil && (
                      <span className="text-[10px] bg-red-500/20 text-red-400 border border-red-500/30 px-1.5 py-0.5 rounded">
                        Customer nudged {new Date(ticket.staleTracking.nudgedAt).toLocaleDateString()}
                      </span>
                    )}
                    {ticket.staleTracking?.flaggedAt && !ticket.staleTracking?.nudgedAt && !ticket.staleTracking?.snoozedUntil && (
                      <span className="text-[10px] bg-amber-500/20 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded">
                        Flagged {new Date(ticket.staleTracking.flaggedAt).toLocaleDateString()}
                      </span>
                    )}
                    {ticket.staleTracking?.adminEmailSentAt && (
                      <span className="text-[10px] bg-orange-500/20 text-orange-400 border border-orange-500/30 px-1.5 py-0.5 rounded">
                        Admin notified
                      </span>
                    )}
                  </div>
                  {ticket.staleTracking && (
                    <div className="text-[10px] text-slate-500 mb-1.5">
                      Tracking since {new Date(ticket.staleTracking.trackingStartedAt).toLocaleDateString()}
                      {' '}({Math.floor((Date.now() - new Date(ticket.staleTracking.trackingStartedAt).getTime()) / (1000 * 60 * 60 * 24))} days)
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-500 shrink-0">Snooze:</span>
                    {([3, 7, 14, 30] as const).map(days => (
                      <button
                        key={days}
                        onClick={() => handleSnooze(days)}
                        disabled={isSnoozing || !myResourceId}
                        className="text-[10px] px-1.5 py-0.5 rounded border border-indigo-500/30 bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        {days}d
                      </button>
                    ))}
                    {!myResourceId && (
                      <span className="text-[10px] text-amber-400 ml-1">Select tech first</span>
                    )}
                  </div>
                </div>
              )}

              {/* Auto-Close Section */}
              {!isTicketClosed && (
                <div className="bg-slate-700/30 border border-slate-600 rounded p-2 shrink-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-slate-400">Auto-Close</span>
                    {ticket.autoCloseAfterDate && (() => {
                      const closeOn = new Date(ticket.autoCloseAfterDate);
                      const days = Math.ceil((closeOn.getTime() - Date.now()) / 86400000);
                      const past = days <= 0;
                      const warned = !!ticket.autoCloseWarnedAt;
                      return (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                          past ? 'bg-red-500/20 text-red-400 border-red-500/30'
                            : warned ? 'bg-orange-500/20 text-orange-400 border-orange-500/30'
                              : 'bg-purple-500/20 text-purple-400 border-purple-500/30'
                        }`}>
                          {past ? 'Closing today' : `${days}d • ${closeOn.toLocaleDateString()}`}
                          {warned && !past && ' • warned'}
                        </span>
                      );
                    })()}
                    {!ticket.autoCloseAfterDate && ticket.autoCloseIfInactiveDays && (
                      <span className="text-[10px] bg-purple-500/20 text-purple-400 border border-purple-500/30 px-1.5 py-0.5 rounded">
                        Inactive {ticket.autoCloseIfInactiveDays}d
                      </span>
                    )}
                  </div>
                  {ticket.autoCloseFlagSetAt && (ticket.autoCloseAfterDate || ticket.autoCloseIfInactiveDays) && (
                    <div className="text-[10px] text-slate-500 mb-1.5">
                      Flag set {new Date(ticket.autoCloseFlagSetAt).toLocaleDateString()}
                      {ticket.autoCloseWarnedAt && ` • Warned ${new Date(ticket.autoCloseWarnedAt).toLocaleDateString()}`}
                    </div>
                  )}
                  {!ticket.autoCloseAfterDate && !ticket.autoCloseIfInactiveDays ? (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] text-slate-500 shrink-0">Close in:</span>
                      {([3, 7, 14] as const).map(days => (
                        <button
                          key={days}
                          onClick={() => handleSetAutoClose(days)}
                          disabled={isAutoClosing}
                          className="text-[10px] px-1.5 py-0.5 rounded border border-purple-500/30 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 disabled:opacity-40 transition-colors"
                        >
                          {days}d
                        </button>
                      ))}
                      <button
                        onClick={() => setShowAutoCloseDate(!showAutoCloseDate)}
                        disabled={isAutoClosing}
                        className="text-[10px] px-1.5 py-0.5 rounded border border-slate-500/30 bg-slate-500/10 text-slate-300 hover:bg-slate-500/20 disabled:opacity-40 transition-colors"
                      >
                        Pick date…
                      </button>
                      {showAutoCloseDate && (
                        <div className="flex items-center gap-1">
                          <input
                            type="date"
                            value={autoCloseDateInput}
                            onChange={e => setAutoCloseDateInput(e.target.value)}
                            min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                            className="text-[10px] px-1 py-0.5 rounded border border-slate-500 bg-slate-800 text-white"
                          />
                          <button
                            onClick={() => autoCloseDateInput && handleSetAutoClose(null, autoCloseDateInput)}
                            disabled={isAutoClosing || !autoCloseDateInput}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-40"
                          >
                            Set
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <button
                        onClick={handleCancelAutoClose}
                        disabled={isAutoClosing}
                        className="text-[10px] px-1.5 py-0.5 rounded border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 disabled:opacity-40 transition-colors"
                      >
                        Cancel auto-close
                      </button>
                      <span className="text-[10px] text-slate-500">
                        Closes to {ticket.autoCloseStatus === 5 || !ticket.autoCloseStatus ? 'Complete' : `status ${ticket.autoCloseStatus}`}
                        {ticket.autoCloseWarnDays !== 0 && ` • warns ${ticket.autoCloseWarnDays ?? 3}d before`}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Diagnostics Section - Collapsible */}
              {!isTicketClosed && (
                <div className="bg-slate-700/30 border border-slate-600 rounded shrink-0">
                  <button
                    onClick={() => setDiagnosticsExpanded(!diagnosticsExpanded)}
                    className="w-full flex items-center justify-between p-2 text-xs font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <svg className={`w-3.5 h-3.5 transition-transform ${diagnosticsExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <span>Diagnostics (FixThis)</span>
                      {diagnosticSession && ['pending', 'collecting', 'analyzing'].includes(diagnosticSession.status) && (
                        <span className="px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 rounded text-[10px] animate-pulse">
                          {diagnosticSession.status}
                        </span>
                      )}
                      {diagnosticSession?.status === 'ready' && (
                        <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 border border-green-500/30 rounded text-[10px]">
                          Results Ready
                        </span>
                      )}
                    </div>
                    {existingSessions.length > 0 && (
                      <span className="text-[10px] text-slate-500">{existingSessions.length} session{existingSessions.length !== 1 ? 's' : ''}</span>
                    )}
                  </button>

                  {diagnosticsExpanded && (
                    <div className="border-t border-slate-600 p-2 space-y-2">
                      {/* Error display */}
                      {diagnosticsError && (
                        <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-2 py-1">
                          {diagnosticsError}
                        </div>
                      )}

                      {/* Loading state */}
                      {diagnosticsLoading && (
                        <div className="text-xs text-slate-400 text-center py-2">
                          Loading devices<LoadingDots />
                        </div>
                      )}

                      {/* Active session display */}
                      {diagnosticSession && (
                        <div className="bg-slate-800/50 border border-slate-600 rounded p-2 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-slate-300">Active Session</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                              diagnosticSession.status === 'ready' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                              diagnosticSession.status === 'failed' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                              diagnosticSession.status === 'expired' ? 'bg-slate-500/20 text-slate-400 border-slate-500/30' :
                              'bg-cyan-500/20 text-cyan-400 border-cyan-500/30'
                            }`}>
                              {diagnosticSession.status}
                            </span>
                          </div>

                          <div className="text-[10px] text-slate-500">
                            Device: <span className="text-slate-400">{diagnosticSession.deviceHostname}</span>
                            <span className="mx-2">•</span>
                            Created: <span className="text-slate-400">{formatTimeAgo(diagnosticSession.createdAt)}</span>
                          </div>

                          {/* One-liner for pending/collecting sessions — tech may need to re-copy */}
                          {['pending', 'collecting'].includes(diagnosticSession.status) && diagnosticSession.sessionToken && (
                            <div className="space-y-1">
                              <span className="text-[10px] text-amber-400">Run this on the target device:</span>
                              <div className="bg-slate-900 rounded p-1.5 font-mono text-[10px] text-cyan-300 break-all select-all cursor-pointer"
                                onClick={() => navigator.clipboard.writeText(`irm https://fixthis.umetech.net/api/fix/${diagnosticSession.sessionToken} | iex`)}
                                title="Click to copy"
                              >
                                irm https://fixthis.umetech.net/api/fix/{diagnosticSession.sessionToken} | iex
                              </div>
                              <span className="text-[10px] text-slate-500">Click to copy</span>
                            </div>
                          )}

                          {/* Polling indicator */}
                          {pollingSession && ['pending', 'collecting', 'analyzing'].includes(diagnosticSession.status) && (
                            <div className="text-[10px] text-cyan-400 flex items-center gap-1">
                              <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Waiting for results...
                            </div>
                          )}

                          {/* Results display */}
                          {diagnosticSession.status === 'ready' && diagnosticSession.findings && (
                            <div className="space-y-2 border-t border-slate-600 pt-2 mt-2">
                              <div className={`text-xs font-medium flex items-center gap-2 ${
                                diagnosticSession.findings.severity === 'critical' ? 'text-red-400' :
                                diagnosticSession.findings.severity === 'warning' ? 'text-amber-400' :
                                'text-green-400'
                              }`}>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  {diagnosticSession.findings.severity === 'critical' ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  ) : diagnosticSession.findings.severity === 'warning' ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  )}
                                </svg>
                                Analysis Results ({diagnosticSession.findings.severity})
                              </div>

                              <div className="bg-slate-900/50 rounded p-2 space-y-2">
                                <div>
                                  <span className="text-[10px] text-slate-500 block mb-0.5">Summary</span>
                                  <p className="text-xs text-slate-300">{diagnosticSession.findings.summary}</p>
                                </div>

                                <div>
                                  <span className="text-[10px] text-slate-500 block mb-0.5">Root Cause</span>
                                  <p className="text-xs text-slate-300">{diagnosticSession.findings.rootCause}</p>
                                </div>

                                {diagnosticSession.findings.recommendations.length > 0 && (
                                  <div>
                                    <span className="text-[10px] text-slate-500 block mb-0.5">Recommendations</span>
                                    <ul className="text-xs text-slate-300 list-disc list-inside space-y-0.5">
                                      {diagnosticSession.findings.recommendations.map((rec, i) => (
                                        <li key={i}>{rec}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {diagnosticSession.findings.systemInfo && (
                                  <div className="text-[10px] text-slate-500 border-t border-slate-700 pt-1 mt-1">
                                    System: {diagnosticSession.findings.systemInfo.os} • {diagnosticSession.findings.systemInfo.hostname}
                                    {diagnosticSession.findings.systemInfo.domain && ` • ${diagnosticSession.findings.systemInfo.domain}`}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Error display for failed sessions */}
                          {diagnosticSession.status === 'failed' && diagnosticSession.errorMessage && (
                            <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded p-2">
                              {diagnosticSession.errorMessage}
                            </div>
                          )}

                          {/* Clear session button */}
                          <button
                            onClick={() => {
                              setDiagnosticSession(null);
                              setPollingSession(false);
                            }}
                            className="text-[10px] text-slate-500 hover:text-slate-400"
                          >
                            Start new session
                          </button>
                        </div>
                      )}

                      {/* Device picker - only show when no active session */}
                      {!diagnosticsLoading && !diagnosticSession && tamDevices.length > 0 && (
                        <>
                          {/* Step 1: Device Selection */}
                          <div className="space-y-1">
                            <label className="text-[10px] text-slate-500">1. Select Device</label>
                            <select
                              value={selectedDevice?.uid || ''}
                              onChange={(e) => {
                                const device = tamDevices.find(d => d.uid === e.target.value);
                                setSelectedDevice(device || null);
                              }}
                              className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs text-white focus:border-cyan-500"
                            >
                              <option value="">Select a device...</option>
                              {tamDevices.map(device => (
                                <option key={device.uid} value={device.uid}>
                                  {device.hostname}
                                  {device.lastUser ? ` (${device.lastUser})` : ''}
                                  {device.status !== 'online' ? ' [offline]' : ''}
                                </option>
                              ))}
                            </select>
                            {selectedDevice && (
                              <div className="text-[10px] text-slate-500">
                                {selectedDevice.operatingSystem}
                                {selectedDevice.intIpAddress && ` • ${selectedDevice.intIpAddress}`}
                                <span className={`ml-2 ${selectedDevice.status === 'online' ? 'text-green-400' : 'text-slate-500'}`}>
                                  ({selectedDevice.status})
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Step 2: Tech Notes */}
                          <div className="space-y-1">
                            <label className="text-[10px] text-slate-500">2. Tech Notes (observations, symptoms)</label>
                            <textarea
                              value={diagnosticTechNotes}
                              onChange={(e) => setDiagnosticTechNotes(e.target.value)}
                              placeholder="Describe what you've observed, symptoms reported, troubleshooting already tried..."
                              rows={2}
                              className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs text-white placeholder-slate-500 focus:border-cyan-500 resize-y"
                            />
                          </div>

                          {/* Step 3: Generate Button */}
                          <button
                            onClick={handleCreateDiagnosticSession}
                            disabled={creatingSession || !selectedDevice}
                            className="w-full flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-600 text-white text-xs font-medium py-1.5 px-3 rounded transition-colors"
                          >
                            {creatingSession ? (
                              <>Creating<LoadingDots /></>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                </svg>
                                Start Diagnostic Session
                              </>
                            )}
                          </button>
                        </>
                      )}

                      {/* Previous sessions */}
                      {existingSessions.length > 0 && !diagnosticSession && (
                        <div className="border-t border-slate-600 pt-2 mt-2">
                          <span className="text-[10px] text-slate-500">Previous Sessions</span>
                          <div className="space-y-1 mt-1">
                            {existingSessions.slice(0, 3).map(session => (
                              <button
                                key={session.id}
                                onClick={() => {
                                  setDiagnosticSession(session);
                                  if (['pending', 'collecting', 'analyzing'].includes(session.status)) {
                                    setPollingSession(true);
                                  }
                                }}
                                className="w-full text-left p-1.5 bg-slate-800/50 border border-slate-700 rounded hover:border-slate-600 transition-colors"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-[10px] text-slate-400">{session.deviceHostname}</span>
                                  <span className={`text-[9px] px-1 py-0.5 rounded ${
                                    session.status === 'ready' ? 'bg-green-500/20 text-green-400' :
                                    session.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                    'bg-slate-500/20 text-slate-400'
                                  }`}>
                                    {session.status}
                                  </span>
                                </div>
                                <div className="text-[9px] text-slate-500">{formatTimeAgo(session.createdAt)}</div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Original email viewer (shown when button clicked) */}
              {emailError && (
                <div className="shrink-0 bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 rounded text-xs">
                  {emailError}
                </div>
              )}
              {showOriginalEmail && originalEmail && (
                <div className="shrink-0 space-y-2">
                  <div className="bg-white rounded overflow-hidden">
                    <iframe
                      srcDoc={originalEmail.bodyHtml}
                      title="Original email"
                      className="w-full border-0"
                      style={{ minHeight: '250px', maxHeight: '400px' }}
                      sandbox="allow-same-origin"
                      onLoad={(e) => {
                        const iframe = e.target as HTMLIFrameElement;
                        try {
                          const height = iframe.contentDocument?.body?.scrollHeight || 250;
                          iframe.style.height = Math.min(height + 20, 400) + 'px';
                        } catch { /* cross-origin */ }
                      }}
                    />
                  </div>
                  {originalEmail.attachments.filter(a => !a.isInline && a.contentBytes).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {originalEmail.attachments
                        .filter(a => !a.isInline && a.contentBytes)
                        .map((att) => (
                          <button
                            key={att.id}
                            onClick={() => downloadAttachment(att)}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-600 hover:bg-slate-500 rounded text-xs text-white transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            {att.name}
                            <span className="text-slate-400">({(att.size / 1024).toFixed(0)}KB)</span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {/* Description - scrollable */}
              <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                <h3 className="text-xs font-medium text-slate-500 mb-1 shrink-0">Description</h3>
                <div className="flex-1 bg-slate-900/50 border border-slate-700 rounded p-2 text-xs text-slate-300 whitespace-pre-wrap overflow-y-auto">
                  {ticket.description || 'No description provided.'}
                </div>
              </div>

              {/* Quick Info Row (expand button moved to header) */}
              <div className="flex items-center gap-4 text-xs text-slate-400 shrink-0 pt-1">
                <span>Queue: <span className="text-slate-300">{ticket.queue?.name || '-'}</span></span>
                <span>Hours: <span className="text-slate-300 font-bold">{ticket.actualHours ?? ticket.hoursWorked ?? '0'}</span></span>
                <span className="text-slate-500">{formatTimeAgo(getTicketDate())}</span>
                {ticket.completedDate && (
                  <span>Completed: <span className="text-green-400">{new Date(ticket.completedDate).toLocaleString()}</span></span>
                )}
              </div>
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Filter controls — hide workflow-rule + from-line stubs by default */}
              {!isLoadingNotes && existingNotes.length > 0 && (() => {
                const hidden = existingNotes.filter(isSystemNoise).length;
                if (hidden === 0) return null;
                return (
                  <div className="flex items-center gap-2 mb-1.5 shrink-0 text-[11px] text-slate-500">
                    <input
                      type="checkbox"
                      id="show-system-notes"
                      checked={showSystemNotes}
                      onChange={(e) => setShowSystemNotes(e.target.checked)}
                      className="cursor-pointer"
                    />
                    <label htmlFor="show-system-notes" className="cursor-pointer">
                      Show {hidden} hidden system note{hidden === 1 ? '' : 's'} (workflow rules, email-from stubs)
                    </label>
                  </div>
                );
              })()}
              {/* Existing Notes List */}
              <div className="flex-1 overflow-y-auto mb-2 min-h-0">
                {isLoadingNotes ? (
                  <div className="text-center text-slate-400 text-xs py-4">Loading notes<LoadingDots /></div>
                ) : existingNotes.length === 0 ? (
                  <div className="text-center text-slate-500 text-xs py-4">No notes yet</div>
                ) : (
                  <div className="space-y-1.5">
                    {[...existingNotes]
                      .filter(n => showSystemNotes || !isSystemNoise(n))
                      .sort((a, b) => new Date(b.createDateTime).getTime() - new Date(a.createDateTime).getTime())
                      .map((note) => {
                        const isExpanded = expandedNoteIds.has(note.id);
                        const isRaw = rawNoteIds.has(note.id);
                        const fullDateTime = new Date(note.createDateTime).toLocaleString(undefined, {
                          year: 'numeric', month: 'short', day: 'numeric',
                          hour: 'numeric', minute: '2-digit',
                        });
                        const { clean, truncated } = cleanNoteBody(note.description);
                        const bodyToShow = isRaw ? (note.description || '') : clean;
                        const isSystem = isSystemNoise(note);
                        // First non-blank line of the CLEANED body — preview is the
                        // actual content, not the quoted history.
                        const preview = clean.split(/\r?\n/).map(l => l.trim()).find(l => l.length > 0) || '';
                        return (
                          <div key={note.id} className={`bg-slate-700/30 border rounded overflow-hidden ${isSystem ? 'border-slate-700/50 opacity-60' : 'border-slate-600'}`}>
                            <button
                              type="button"
                              onClick={() => setExpandedNoteIds(prev => {
                                const next = new Set(prev);
                                if (next.has(note.id)) next.delete(note.id); else next.add(note.id);
                                return next;
                              })}
                              className="w-full text-left px-3 py-2 hover:bg-slate-700/50 transition-colors flex items-center gap-2"
                            >
                              <span className="text-slate-400 text-xs shrink-0" aria-hidden="true">
                                {isExpanded ? '▾' : '▸'}
                              </span>
                              <span className="text-xs font-semibold text-slate-200 shrink-0">
                                {note.creatorName || 'System'}
                              </span>
                              <span className="text-[11px] text-slate-400 shrink-0 tabular-nums">{fullDateTime}</span>
                              <span className="text-[11px] text-slate-600 shrink-0">·</span>
                              <span className="text-[11px] text-slate-500 shrink-0">{formatTimeAgo(note.createDateTime)}</span>
                              {note.noteType === 2 && (
                                <span className="text-[10px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded shrink-0">Customer Visible</span>
                              )}
                              {isSystem && (
                                <span className="text-[10px] bg-slate-600/30 text-slate-400 px-1.5 py-0.5 rounded shrink-0">System</span>
                              )}
                              {!isExpanded && preview && (
                                <span className="text-xs text-slate-400 truncate ml-1 min-w-0">{preview}</span>
                              )}
                            </button>
                            {isExpanded && (
                              <div className="px-3 pb-3 pt-1 border-t border-slate-700/50">
                                <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap break-words">{bodyToShow || '(empty)'}</p>
                                {truncated && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRawNoteIds(prev => {
                                        const next = new Set(prev);
                                        if (next.has(note.id)) next.delete(note.id); else next.add(note.id);
                                        return next;
                                      });
                                    }}
                                    className="mt-2 text-[11px] text-slate-500 hover:text-slate-300 transition-colors"
                                  >
                                    {isRaw ? '◂ Hide quoted thread' : 'Show full quoted thread ▸'}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>

              {/* Add Note Form */}
              <div className="border-t border-slate-700 pt-2 shrink-0 space-y-2">
                {!myResourceId && (
                  <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1">
                    Could not detect your technician identity — please reload.
                  </div>
                )}
                <div className="flex gap-2 items-start">
                  <select
                    value={noteType}
                    onChange={(e) => setNoteType(parseInt(e.target.value))}
                    className="bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-white text-sm focus:border-blue-500"
                  >
                    <option value={1}>Internal</option>
                    <option value={2}>Customer Visible</option>
                  </select>
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add a note..."
                    rows={3}
                    className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-white text-sm placeholder-slate-500 focus:border-blue-500 resize-y min-h-[60px]"
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && e.ctrlKey && noteText.trim() && handleAddNote()}
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={isLoading || !noteText.trim()}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white text-sm font-medium px-4 py-1.5 rounded transition-colors"
                  >
                    {isLoading ? '...' : 'Add'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Time Entry Tab */}
          {activeTab === 'time' && (
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Pending Completion Notice */}
              {pendingCompletion && (
                <div className="bg-amber-600/20 border border-amber-500/50 rounded p-2 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4 text-amber-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-xs text-amber-300">Add a time entry below to complete this ticket</span>
                  <button
                    onClick={() => { setPendingCompletion(false); setCloseAfterTime(false); setSuccess(null); }}
                    className="ml-auto text-amber-400 hover:text-amber-300 text-xs underline"
                  >
                    Cancel
                  </button>
                </div>
              )}
              {/* Existing Time Entries List */}
              <div className="flex-1 overflow-y-auto mb-2 min-h-0">
                {isLoadingTimeEntries ? (
                  <div className="text-center text-slate-400 text-xs py-4">Loading time entries<LoadingDots /></div>
                ) : existingTimeEntries.length === 0 ? (
                  <div className="text-center text-slate-500 text-xs py-4">No time entries yet</div>
                ) : (
                  <div className="space-y-2">
                    {existingTimeEntries.map((entry) => (
                      <div key={entry.id} className="bg-slate-700/30 border border-slate-600 rounded p-2">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-slate-300">{entry.resourceName || 'Unknown'}</span>
                            <span className="text-xs text-slate-500">{new Date(entry.dateWorked).toLocaleDateString()}</span>
                          </div>
                          <span className="text-xs font-bold text-green-400">{entry.hoursWorked}h</span>
                        </div>
                        {entry.summaryNotes && (
                          <p className="text-xs text-slate-400 truncate">{entry.summaryNotes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Time Entry Form */}
              <div className="border-t border-slate-700 pt-2 shrink-0 space-y-2">
                <div className="flex gap-2 items-center">
                  {/* Show logged-in user's name (not editable — you always log time as yourself) */}
                  <span className="bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-white text-sm">
                    {getMyResource() ? `${getMyResource()!.firstName} ${getMyResource()!.lastName}` : 'Unknown tech'}
                  </span>
                  <input
                    type="date"
                    value={dateWorked}
                    onChange={(e) => setDateWorked(e.target.value)}
                    className="bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-white text-sm focus:border-blue-500"
                  />
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      step="0.25"
                      min="0"
                      max="24"
                      value={hoursWorked}
                      onChange={(e) => setHoursWorked(e.target.value)}
                      placeholder="0"
                      className="w-16 bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-white text-sm placeholder-slate-500 focus:border-blue-500"
                    />
                    <span className="text-sm text-slate-400">hrs</span>
                  </div>
                </div>
                {/* Project selector (if company has projects) */}
                {(loadingProjects || projects.length > 0) && (
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-slate-400 shrink-0">Project:</span>
                    {loadingProjects ? (
                      <span className="text-xs text-slate-500">Loading...</span>
                    ) : (
                      <select
                        value={selectedProject?.projectId || ''}
                        onChange={(e) => {
                          const projectId = parseInt(e.target.value);
                          const project = projects.find(p => p.projectId === projectId);
                          setSelectedProject(project || null);
                        }}
                        className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs focus:border-blue-500"
                      >
                        <option value="">Select project...</option>
                        {projects.map(p => (
                          <option key={p.projectId} value={p.projectId}>
                            {p.projectName}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
                {/* Bid-job selector */}
                {(loadingBidJobs || bidJobs.length > 0) && (
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-slate-400 shrink-0">Bid-Job:</span>
                    {loadingBidJobs ? (
                      <span className="text-xs text-slate-500">Loading...</span>
                    ) : (
                      <select
                        value={selectedBidJob?.id || ''}
                        onChange={(e) => {
                          const bidJob = bidJobs.find(bj => bj.id === e.target.value);
                          setSelectedBidJob(bidJob || null);
                        }}
                        className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs focus:border-blue-500"
                      >
                        <option value="">Select bid-job...</option>
                        {bidJobs.map(bj => (
                          <option key={bj.id} value={bj.id}>
                            {bj.bidJobNumber} | {bj.bidJobDescription}
                            {bj.isDefault ? ' (Default)' : ''}
                            {bj.isCommon ? ' [Common]' : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
                {/* Mileage & Expense checkboxes */}
                <div className="flex gap-4 items-center">
                  <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-300">
                    <input
                      type="checkbox"
                      checked={showMileage}
                      onChange={(e) => setShowMileage(e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-900 text-teal-500"
                    />
                    Mileage
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-300">
                    <input
                      type="checkbox"
                      checked={showExpense}
                      onChange={(e) => setShowExpense(e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-900 text-amber-500"
                    />
                    Customer Expense
                  </label>
                </div>

                {/* Mileage panel */}
                {showMileage && (
                  <div className="bg-teal-900/20 border border-teal-700/40 rounded p-2 space-y-2 text-xs">
                    <div className="flex gap-2 items-center">
                      <span className="text-slate-400 w-12 shrink-0">Start:</span>
                      <select
                        value={mileageStart}
                        onChange={(e) => {
                          setMileageStart(e.target.value);
                          setEstimatedMiles(null);
                        }}
                        className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                      >
                        <option value="Office">Office</option>
                        {ticket.companyName && <option value={ticket.companyName}>{ticket.companyName}</option>}
                        <option value="Other">Other</option>
                      </select>
                      {mileageStart === 'Other' && (
                        <input
                          type="text"
                          placeholder="Location..."
                          onChange={(e) => setMileageStart(e.target.value || 'Other')}
                          className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                        />
                      )}
                    </div>
                    <div className="flex gap-2 items-center">
                      <span className="text-slate-400 w-12 shrink-0">Dest:</span>
                      <input
                        type="text"
                        value={mileageDestination}
                        onChange={(e) => setMileageDestination(e.target.value)}
                        placeholder={ticket.companyName || 'Destination...'}
                        className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                      />
                    </div>
                    {estimatedMiles !== null && (
                      <div className="text-slate-400">
                        Approx distance: <span className="text-teal-400">{estimatedMiles} mi</span>
                      </div>
                    )}
                    <div className="flex gap-2 items-center">
                      <span className="text-slate-400 w-12 shrink-0">Odom:</span>
                      <input
                        type="number"
                        value={odometerStart}
                        onChange={(e) => setOdometerStart(e.target.value)}
                        placeholder="Start"
                        className="w-24 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                      />
                      <span className="text-slate-500">to</span>
                      <input
                        type="number"
                        value={odometerEnd}
                        onChange={(e) => setOdometerEnd(e.target.value)}
                        placeholder="End"
                        className="w-24 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                      />
                      {odometerStart && odometerEnd && parseFloat(odometerEnd) > parseFloat(odometerStart) && (
                        <span className={`${
                          estimatedMiles && Math.abs((parseFloat(odometerEnd) - parseFloat(odometerStart)) - estimatedMiles) / estimatedMiles > 0.2
                            ? 'text-amber-400'
                            : 'text-teal-400'
                        }`}>
                          = {(parseFloat(odometerEnd) - parseFloat(odometerStart)).toFixed(1)} mi
                          {estimatedMiles && Math.abs((parseFloat(odometerEnd) - parseFloat(odometerStart)) - estimatedMiles) / estimatedMiles > 0.2 && ' (>20% off est.)'}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 items-center">
                      <span className="text-slate-400 w-12 shrink-0">Item:</span>
                      <input
                        type="text"
                        value={mileageItem}
                        onChange={(e) => setMileageItem(e.target.value)}
                        placeholder={`Drive to ${ticket.companyName || 'client'}`}
                        className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                      />
                    </div>
                    {odometerStart && odometerEnd && parseFloat(odometerEnd) > parseFloat(odometerStart) && (
                      <div className="text-slate-400">
                        Reimbursement: <span className="text-green-400">${((parseFloat(odometerEnd) - parseFloat(odometerStart)) * mileageRate).toFixed(2)}</span>
                        <span className="text-slate-500 ml-1">(@ ${mileageRate}/mi)</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Expense panel */}
                {showExpense && (
                  <div className="bg-amber-900/20 border border-amber-700/40 rounded p-2 space-y-2 text-xs">
                    <div className="flex gap-2 items-center">
                      <span className="text-slate-400 w-12 shrink-0">Item:</span>
                      <input
                        type="text"
                        value={expenseItem}
                        onChange={(e) => setExpenseItem(e.target.value)}
                        placeholder="Parking, Education, etc..."
                        className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                      />
                    </div>
                    <div className="flex gap-2 items-center">
                      <span className="text-slate-400 w-12 shrink-0">$:</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={expenseAmount}
                        onChange={(e) => setExpenseAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-28 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                      />
                      <span className="text-slate-500">Client: {ticket.companyName || 'N/A'}</span>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 items-start">
                  <textarea
                    value={timeNotes}
                    onChange={(e) => setTimeNotes(e.target.value)}
                    placeholder="Work summary..."
                    rows={3}
                    className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-white text-sm placeholder-slate-500 focus:border-blue-500 resize-y min-h-[60px]"
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && e.ctrlKey && timeNotes.trim() && myResourceId && handleAddTimeEntry(closeAfterTime)}
                  />
                  <label className="flex items-center gap-1 cursor-pointer text-xs text-slate-400 shrink-0">
                    <input
                      type="checkbox"
                      checked={closeAfterTime}
                      onChange={(e) => setCloseAfterTime(e.target.checked)}
                      className="w-3 h-3 rounded border-slate-600 bg-slate-900 text-blue-500"
                    />
                    Complete
                  </label>
                  <button
                    onClick={() => handleAddTimeEntry(closeAfterTime)}
                    disabled={isLoading || !myResourceId || (
                      // Need either: time entry data, close flag, or mileage/expense
                      !closeAfterTime &&
                      !(showMileage && odometerStart && odometerEnd && parseFloat(odometerEnd) > parseFloat(odometerStart)) &&
                      !(showExpense && expenseItem && expenseAmount && parseFloat(expenseAmount) > 0) &&
                      (!timeNotes.trim() || !hoursWorked || parseFloat(hoursWorked) <= 0)
                    ) || (projects.length > 0 && !selectedProject && parseFloat(hoursWorked || '0') > 0)}
                    className={`text-xs font-medium px-3 py-1 rounded transition-colors ${
                      closeAfterTime
                        ? 'bg-green-600 hover:bg-green-700 disabled:bg-slate-600'
                        : 'bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600'
                    } text-white`}
                  >
                    {isLoading ? '...' : 'Add'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===== INTAKE TAB ===== */}
          {activeTab === 'intake' && (
            <div className="flex-1 overflow-y-auto space-y-3">
              {/* Send Form Section */}
              <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                <h4 className="text-xs font-semibold text-slate-300 mb-2">Send Intake Form</h4>

                {/* Contact dropdown */}
                <div className="mb-2">
                  <label className="block text-[10px] text-slate-400 mb-1">End User</label>
                  {loadingContacts ? (
                    <div className="text-slate-400 text-xs py-1">Loading contacts...</div>
                  ) : (
                    <select
                      value={showNewContactForm ? '__new__' : (selectedContactId ?? '')}
                      onChange={e => {
                        const val = e.target.value;
                        if (val === '__new__') {
                          setShowNewContactForm(true);
                          setSelectedContactId(null);
                          setIntakeEndUserName('');
                          setIntakeEndUserEmail('');
                        } else if (val === '') {
                          setShowNewContactForm(false);
                          setSelectedContactId(null);
                          setIntakeEndUserName('');
                          setIntakeEndUserEmail('');
                        } else {
                          setShowNewContactForm(false);
                          const contact = companyContacts.find(c => c.id === Number(val));
                          if (contact) {
                            setSelectedContactId(contact.id);
                            setIntakeEndUserName(`${contact.firstName} ${contact.lastName}`);
                            setIntakeEndUserEmail(contact.email);
                          }
                        }
                      }}
                      className="w-full bg-slate-700 text-white text-xs px-2 py-1.5 rounded border border-slate-600 focus:border-blue-500 outline-none"
                    >
                      <option value="">Select end user...</option>
                      {companyContacts.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.firstName} {c.lastName}{c.email ? ` — ${c.email}` : ''}
                        </option>
                      ))}
                      <option value="__new__">+ Add new contact...</option>
                    </select>
                  )}
                </div>

                {/* New contact form (shown when "Add new" selected) */}
                {showNewContactForm && (
                  <div className="mb-2 p-2 bg-slate-700/50 rounded border border-slate-600 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="First name"
                        value={newContactFirstName}
                        onChange={e => setNewContactFirstName(e.target.value)}
                        className="bg-slate-700 text-white text-xs px-2 py-1.5 rounded border border-slate-600 focus:border-blue-500 outline-none"
                      />
                      <input
                        type="text"
                        placeholder="Last name"
                        value={newContactLastName}
                        onChange={e => setNewContactLastName(e.target.value)}
                        className="bg-slate-700 text-white text-xs px-2 py-1.5 rounded border border-slate-600 focus:border-blue-500 outline-none"
                      />
                    </div>
                    <input
                      type="email"
                      placeholder="Email"
                      value={newContactEmail}
                      onChange={e => setNewContactEmail(e.target.value)}
                      className="w-full bg-slate-700 text-white text-xs px-2 py-1.5 rounded border border-slate-600 focus:border-blue-500 outline-none"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          // Explicit undefined/type check — companyId=0 is a VALID AT id (Umetech's own tenant
                          // record uses id 0). Prior `!companyId` guard silently swallowed the click for any
                          // ticket where companyId=0, so the button did nothing with no feedback.
                          if (typeof ticket?.companyId !== 'number' || !newContactFirstName.trim() || !newContactLastName.trim() || !newContactEmail.trim()) {
                            setError('Missing required fields (company / first / last / email).');
                            return;
                          }
                          setError(null);
                          setCreatingContact(true);
                          try {
                            const created = await createContact({
                              companyId: ticket.companyId,
                              firstName: newContactFirstName.trim(),
                              lastName: newContactLastName.trim(),
                              email: newContactEmail.trim(),
                            });
                            // Refresh contacts and auto-select
                            const refreshed = await getCompanyContacts(ticket.companyId, true);
                            setCompanyContacts(refreshed);
                            setSelectedContactId(created.id);
                            setIntakeEndUserName(`${created.firstName} ${created.lastName}`);
                            setIntakeEndUserEmail(created.email);
                            setShowNewContactForm(false);
                            setNewContactFirstName('');
                            setNewContactLastName('');
                            setNewContactEmail('');
                            setSuccess('Contact created');
                          } catch (err) {
                            setError(err instanceof Error ? err.message : 'Failed to create contact');
                          } finally {
                            setCreatingContact(false);
                          }
                        }}
                        disabled={creatingContact || typeof ticket?.companyId !== 'number' || !newContactFirstName.trim() || !newContactLastName.trim() || !newContactEmail.trim()}
                        className="text-xs font-medium px-2 py-1 rounded bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white transition-colors"
                      >
                        {creatingContact ? 'Creating...' : 'Create & Select'}
                      </button>
                      <button
                        onClick={() => {
                          setShowNewContactForm(false);
                          setNewContactFirstName('');
                          setNewContactLastName('');
                          setNewContactEmail('');
                        }}
                        className="text-xs font-medium px-2 py-1 rounded bg-slate-600 hover:bg-slate-500 text-white transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Show selected contact name/email (read-only) when a contact is selected */}
                {!showNewContactForm && selectedContactId && intakeEndUserName && (
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div className="text-xs text-slate-300 px-2 py-1.5">
                      <span className="text-slate-500">Name:</span> {intakeEndUserName}
                    </div>
                    <div className="text-xs text-slate-300 px-2 py-1.5">
                      <span className="text-slate-500">Email:</span> {intakeEndUserEmail}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  {/* Save contact to ticket (when selected contact differs from ticket's current contact) */}
                  {selectedContactId && selectedContactId !== ticket?.contactId && (
                    <button
                      onClick={async () => {
                        if (!ticket || !selectedContactId) return;
                        setSavingContactToTicket(true);
                        setError(null);
                        try {
                          await updateTicket(ticket.autoTaskId, { contactID: selectedContactId });
                          setTicket({
                            ...ticket,
                            contactId: selectedContactId,
                            contactName: intakeEndUserName,
                            contactEmail: intakeEndUserEmail,
                          });
                          setSuccess('Contact saved to ticket');
                          onTicketUpdated?.();
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Failed to save contact to ticket');
                        } finally {
                          setSavingContactToTicket(false);
                        }
                      }}
                      disabled={savingContactToTicket}
                      className="text-xs font-medium px-3 py-1.5 rounded bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white transition-colors"
                    >
                      {savingContactToTicket ? 'Saving...' : 'Save to Ticket'}
                    </button>
                  )}

                  <button
                    onClick={handleSendIntakeForm}
                    disabled={isSendingIntake || !intakeEndUserName.trim() || !intakeEndUserEmail.trim() || showNewContactForm}
                    className="text-xs font-medium px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white transition-colors"
                  >
                    {isSendingIntake ? 'Sending...' : 'Send Intake Form'}
                  </button>
                </div>
              </div>

              {/* Sessions List */}
              {isLoadingIntake ? (
                <div className="text-center text-slate-400 text-xs py-4">Loading sessions...</div>
              ) : intakeSessions.length === 0 ? (
                <div className="text-center text-slate-500 text-xs py-4">No intake sessions yet</div>
              ) : (
                <div className="space-y-2">
                  {intakeSessions.map(s => (
                    <div key={s.id} className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden">
                      {/* Session header row */}
                      <div
                        className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-700/50"
                        onClick={() => {
                          setExpandedIntakeId(expandedIntakeId === s.id ? null : s.id);
                          if (s.submission?.detectedDeviceId !== undefined) {
                            loadScanData(s.submission.detectedDeviceId);
                          }
                        }}
                      >
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          s.status === 'submitted' ? 'bg-green-500/20 text-green-400' :
                          s.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          s.status === 'expired' ? 'bg-slate-500/20 text-slate-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {s.status}
                        </span>
                        <span className="text-xs text-white truncate flex-1">{s.endUserName}</span>
                        {s.relinkRequestedAt && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/20 text-amber-400">
                            Relink requested
                          </span>
                        )}
                        <span className="text-[10px] text-slate-500">{new Date(s.createdAt).toLocaleDateString()}</span>
                        <span className="text-slate-500 text-xs">{expandedIntakeId === s.id ? '\u25B2' : '\u25BC'}</span>
                      </div>

                      {/* Expanded session details */}
                      {expandedIntakeId === s.id && (
                        <div className="px-3 pb-3 space-y-2 border-t border-slate-700">
                          {s.status === 'submitted' && s.submission ? (
                            <>
                              {/* Field responses */}
                              <div className="mt-2">
                                <h5 className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Responses</h5>
                                {s.submission.fields.map(f => (
                                  <div key={f.fieldId} className="text-xs mb-1">
                                    <span className="text-slate-400">{f.fieldLabel}:</span>{' '}
                                    <span className="text-white">{Array.isArray(f.value) ? f.value.join(', ') : String(f.value)}</span>
                                  </div>
                                ))}
                              </div>

                              {/* Device panel */}
                              {s.submission.detectedHostname ? (
                                <div className="bg-slate-700/50 rounded p-2">
                                  <div className="flex items-center gap-2">
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-500/20 text-green-400">
                                      {s.submission.detectedHostname}
                                    </span>
                                    {s.submission.deviceDetectionMethod === 'font' && (
                                      <span className="text-[10px] text-slate-500">auto-detected</span>
                                    )}
                                  </div>

                                  {/* System health info */}
                                  {s.submission.systemInfo && (
                                    <div className="mt-2 text-xs space-y-0.5">
                                      {s.submission.systemInfo.operatingSystem && (
                                        <p className="text-slate-300"><span className="text-slate-500">OS:</span> {s.submission.systemInfo.operatingSystem}</p>
                                      )}
                                      {s.submission.systemInfo.manufacturer && s.submission.systemInfo.model && (
                                        <p className="text-slate-300"><span className="text-slate-500">HW:</span> {s.submission.systemInfo.manufacturer} {s.submission.systemInfo.model}</p>
                                      )}
                                      {s.submission.systemInfo.processor && (
                                        <p className="text-slate-300"><span className="text-slate-500">CPU:</span> {s.submission.systemInfo.processor}</p>
                                      )}
                                      {s.submission.systemInfo.totalRamGB && (
                                        <p className="text-slate-300"><span className="text-slate-500">RAM:</span> {s.submission.systemInfo.totalRamGB} GB</p>
                                      )}
                                      {s.submission.systemInfo.uptimeDays !== undefined && (
                                        <p className={`${
                                          s.submission.systemInfo.uptimeDays > 7 ? 'text-red-400' :
                                          s.submission.systemInfo.uptimeDays > 3 ? 'text-amber-400' : 'text-green-400'
                                        }`}>
                                          <span className="text-slate-500">Uptime:</span> {s.submission.systemInfo.uptimeDays} days
                                        </p>
                                      )}

                                      {/* Disk bars */}
                                      {s.submission.systemInfo.diskDrives?.map(disk => (
                                        <div key={disk.drive} className="flex items-center gap-1">
                                          <span className="text-slate-500 w-6">{disk.drive}</span>
                                          <div className="flex-1 bg-slate-600 rounded-full h-1.5">
                                            <div
                                              className={`h-1.5 rounded-full ${
                                                disk.freePercent < 10 ? 'bg-red-500' :
                                                disk.freePercent < 25 ? 'bg-amber-500' : 'bg-green-500'
                                              }`}
                                              style={{ width: `${100 - disk.freePercent}%` }}
                                            />
                                          </div>
                                          <span className="text-slate-500 text-[10px] w-14 text-right">{disk.freeGB.toFixed(0)} GB free</span>
                                        </div>
                                      ))}

                                      {/* AV + patches */}
                                      {s.submission.systemInfo.antivirusProduct && (
                                        <p className="text-slate-300"><span className="text-slate-500">AV:</span> {s.submission.systemInfo.antivirusProduct} — {s.submission.systemInfo.antivirusStatus}</p>
                                      )}

                                      {/* Warnings */}
                                      {s.submission.systemInfo.warnings && s.submission.systemInfo.warnings.length > 0 && (
                                        <div className="mt-1 space-y-0.5">
                                          {s.submission.systemInfo.warnings.map((w, i) => (
                                            <p key={i} className="text-amber-400 text-[10px]">{w}</p>
                                          ))}
                                        </div>
                                      )}

                                      {s.submission.systemInfo.collectedAt && (
                                        <p className="text-[10px] text-slate-600 mt-1">
                                          Data from: {new Date(s.submission.systemInfo.collectedAt).toLocaleString()}
                                        </p>
                                      )}
                                    </div>
                                  )}

                                  {/* SecureScan data */}
                                  {s.submission.detectedDeviceId !== undefined && intakeScanData[s.submission.detectedDeviceId] && (
                                    <div className="mt-2 border-t border-slate-600 pt-2">
                                      <h6 className="text-[10px] font-semibold text-slate-400 uppercase mb-1">SecureScan Data</h6>
                                      {(() => {
                                        const scan = intakeScanData[s.submission.detectedDeviceId!];
                                        if (!scan) return null;
                                        return (
                                          <div className="text-xs space-y-0.5">
                                            {scan.securityConfig && (
                                              <div className="flex gap-2 flex-wrap">
                                                {scan.securityConfig.bitLockerEnabled !== undefined && (
                                                  <span className={`text-[10px] px-1 rounded ${scan.securityConfig.bitLockerEnabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                                    BitLocker {scan.securityConfig.bitLockerEnabled ? 'ON' : 'OFF'}
                                                  </span>
                                                )}
                                                {scan.securityConfig.firewallEnabled !== undefined && (
                                                  <span className={`text-[10px] px-1 rounded ${scan.securityConfig.firewallEnabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                                    Firewall {scan.securityConfig.firewallEnabled ? 'ON' : 'OFF'}
                                                  </span>
                                                )}
                                                {scan.securityConfig.defenderEnabled !== undefined && (
                                                  <span className={`text-[10px] px-1 rounded ${scan.securityConfig.defenderEnabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                                    Defender {scan.securityConfig.defenderEnabled ? 'ON' : 'OFF'}
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                            {scan.patchStatus?.missingPatches !== undefined && scan.patchStatus.missingPatches > 0 && (
                                              <p className="text-amber-400">{scan.patchStatus.missingPatches} missing patches</p>
                                            )}
                                            {scan.localAdmins && scan.localAdmins.length > 0 && (
                                              <p className="text-slate-300"><span className="text-slate-500">Local admins:</span> {scan.localAdmins.join(', ')}</p>
                                            )}
                                            {scan.findings && scan.findings.length > 0 && (
                                              <div className="mt-1">
                                                {scan.findings.slice(0, 5).map((f, i) => (
                                                  <p key={i} className={`text-[10px] ${
                                                    f.severity === 'critical' || f.severity === 'high' ? 'text-red-400' :
                                                    f.severity === 'medium' ? 'text-amber-400' : 'text-slate-400'
                                                  }`}>
                                                    [{f.severity}] {f.description}
                                                  </p>
                                                ))}
                                              </div>
                                            )}
                                            <p className="text-[10px] text-slate-600">Last scan: {new Date(scan.lastScanDate).toLocaleString()}</p>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}

                                  {/* Action buttons */}
                                  <div className="mt-2 flex gap-2">
                                    {s.submission.screenConnectGuid && (
                                      <button
                                        onClick={() => {
                                          const scWindow = window.open(`${screenConnectBaseUrl}/Host#Access/All%20Machines/${s.submission!.screenConnectGuid}/Join`);
                                          setTimeout(() => { try { scWindow?.close(); } catch(e) {} }, 60000);
                                        }}
                                        className="text-[10px] font-medium px-2 py-1 rounded bg-purple-600 hover:bg-purple-700 text-white transition-colors"
                                      >
                                        Remote In
                                      </button>
                                    )}
                                    {s.submission.detectedDeviceId !== undefined && (
                                      <button
                                        onClick={async () => {
                                          try {
                                            await triggerDeviceScan(s.submission!.detectedDeviceId!);
                                            setSuccess('Scan triggered, results in ~2 min');
                                          } catch (err) {
                                            setError(err instanceof Error ? err.message : 'Failed to trigger scan');
                                          }
                                        }}
                                        className="text-[10px] font-medium px-2 py-1 rounded bg-slate-600 hover:bg-slate-500 text-white transition-colors"
                                      >
                                        Run Fresh Scan
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <p className="text-xs text-slate-500 mt-1">No device detected</p>
                              )}

                              {s.submission.notePostedToAutoTask && (
                                <p className="text-[10px] text-green-500 mt-1">Posted to AutoTask</p>
                              )}
                            </>
                          ) : (
                            <div className="mt-2 flex items-center gap-2">
                              <p className="text-xs text-slate-400">Sent to {s.endUserEmail}</p>
                              {s.status === 'pending' && (
                                <button
                                  onClick={() => handleInvalidateIntake(s.id)}
                                  className="text-[10px] font-medium px-2 py-0.5 rounded bg-red-600/20 text-red-400 hover:bg-red-600/30 transition-colors"
                                >
                                  Invalidate
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
